/**
 * Elite Batch 10 Part 1 — IT-DevOps (848-862) + Marketing start (863-867)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after trigger MUST extract named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}

function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{sheet_name}!A1:append?valueInputOption=USER_ENTERED",
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 10 PART 1 — IT-DevOps 848-862, Marketing 863-867
// =============================================================================

export const ELITE_BATCH10_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 848. PRESS RELEASE DISTRIBUTION WORKFLOW
  // ===================================================================
  "Press Release Distribution Workflow": {
    nodes: [
      trigger("CMS Approval Webhook", "webhook_app", {
        source: "WordPress / CMS",
        event: "post.approved",
        howItStarts:
          "A press release is approved in the CMS and ready for distribution",
        setup:
          "In your CMS, add a webhook action that fires on post approval for the Press Release category. Include post_id, title, author, and category in the payload.",
        inputFields: [
          { name: "post_id", type: "string", example: "pr-2026-0142" },
          {
            name: "pr_title",
            type: "string",
            example: "Acme Corp Announces Q1 Results",
          },
          { name: "pr_author", type: "string", example: "Jane Smith" },
          { name: "pr_category", type: "string", example: "earnings" },
        ],
      }),

      // 2. Content fetch + snapshot
      n("2", "hitApi", "Fetch PR content from CMS", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://your-cms.com/wp-json/wp/v2/posts/{post_id}",
          headers: [{ key: "Authorization", value: "Bearer {cms_token}" }],
          variables: [
            {
              name: "content.rendered",
              customName: "pr_body_html",
              type: "string",
            },
            {
              name: "excerpt.rendered",
              customName: "pr_excerpt",
              type: "string",
            },
            { name: "date", customName: "pr_publish_date", type: "string" },
            {
              name: "meta.pr_contact_email",
              customName: "pr_contact_email",
              type: "string",
            },
            {
              name: "meta.pr_boilerplate",
              customName: "pr_boilerplate",
              type: "string",
            },
            {
              name: "featured_media_url",
              customName: "pr_hero_image",
              type: "string",
            },
          ],
        },
      }),

      // 3. Distribution list validation
      n("3", "runCode", "Validate distribution lists", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and segment distribution lists by category
const mediaLists = {
  earnings: ["finance@reuters.com", "markets@bloomberg.com", "biz@ap.org"],
  product: ["tech@techcrunch.com", "news@theverge.com", "tips@engadget.com"],
  partnership: ["deals@businesswire.com", "partnerships@forbes.com"],
  general: ["press@prnewswire.com", "news@globenewswire.com"]
};
const category = vars.pr_category || "general";
const targetList = mediaLists[category] || mediaLists.general;
const result = {
  distribution_emails: targetList.join(","),
  distribution_count: targetList.length,
  wire_service: category === "earnings" ? "businesswire" : "prnewswire",
  is_valid_list: targetList.length > 0
};
return result;`,
          outputVariables: [
            { name: "distribution_emails", type: "string" },
            { name: "distribution_count", type: "number" },
            { name: "wire_service", type: "string" },
            { name: "is_valid_list", type: "boolean" },
          ],
        },
      }),

      // 4. Wire format conversion
      n("4", "runCode", "Convert to wire format (AP style)", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Convert HTML press release to AP wire format
const stripHtml = vars.pr_body_html.replace(/<[^>]*>/g, "");
const dateline = new Date(vars.pr_publish_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const wireBody = "FOR IMMEDIATE RELEASE\\n\\n" + vars.pr_title.toUpperCase() + "\\n\\n" + dateline + " -- " + stripHtml + "\\n\\n###\\n\\n" + vars.pr_boilerplate + "\\n\\nMedia Contact: " + vars.pr_contact_email;
const result = {
  wire_formatted_body: wireBody,
  wire_headline: vars.pr_title.toUpperCase(),
  wire_dateline: dateline,
  word_count: stripHtml.split(/\\s+/).length
};
return result;`,
          outputVariables: [
            { name: "wire_formatted_body", type: "string" },
            { name: "wire_headline", type: "string" },
            { name: "wire_dateline", type: "string" },
            { name: "word_count", type: "number" },
          ],
        },
      }),

      // 5. PR Newswire submission
      n("5", "hitApi", "Submit to PR Newswire", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.prnewswire.com/v1/releases",
          headers: [
            { key: "Authorization", value: "Bearer {prnewswire_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "headline", value: "{wire_headline}" },
            { key: "body", value: "{wire_formatted_body}" },
            { key: "dateline", value: "{wire_dateline}" },
            { key: "category", value: "{pr_category}" },
            { key: "contact_email", value: "{pr_contact_email}" },
          ],
          variables: [
            {
              name: "release_id",
              customName: "wire_release_id",
              type: "string",
            },
            { name: "status", customName: "wire_status", type: "string" },
            {
              name: "distribution_url",
              customName: "wire_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Direct media pitch emails via SendGrid
      n("6", "hitApi", "Pitch media contacts (SendGrid)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{distribution_emails}"}],"subject":"[PRESS] {pr_title}"}]',
            },
            {
              key: "from",
              value: '{"email":"{pr_contact_email}","name":"Press Office"}',
            },
            {
              key: "content",
              value:
                '[{"type":"text/html","value":"<h2>{pr_title}</h2><p>{pr_excerpt}</p><p>Full release: {wire_url}</p><p>{pr_boilerplate}</p>"}]',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "pitch_send_status",
              type: "number",
            },
          ],
        },
      }),

      // 7. Publish to company newsroom (WordPress)
      n("7", "hitApi", "Publish to company newsroom", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://your-company.com/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{pr_title}" },
            { key: "content", value: "{pr_body_html}" },
            { key: "status", value: "publish" },
            { key: "categories", value: "[12]" },
            { key: "featured_media", value: "{pr_hero_image}" },
          ],
          variables: [
            { name: "id", customName: "newsroom_post_id", type: "string" },
            { name: "link", customName: "newsroom_url", type: "string" },
          ],
        },
      }),

      // 8. Social channel sharing via Buffer
      n("8", "hitApi", "Schedule social posts (Buffer)", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "text", value: "{pr_title} — Read more: {newsroom_url}" },
            { key: "profile_ids[]", value: "{buffer_profile_ids}" },
            { key: "media[link]", value: "{newsroom_url}" },
            { key: "media[description]", value: "{pr_excerpt}" },
            { key: "media[picture]", value: "{pr_hero_image}" },
          ],
          variables: [
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
            { name: "success", customName: "buffer_success", type: "boolean" },
          ],
        },
      }),

      // 9. Pickup monitoring via SerpAPI
      n("9", "hitApi", "Monitor media pickups (SerpAPI)", 1960, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=%22{pr_title}%22&tbm=nws&api_key={serpapi_key}",
          headers: [],
          variables: [
            {
              name: "news_results.length",
              customName: "pickup_count",
              type: "number",
            },
            {
              name: "news_results[0].title",
              customName: "first_pickup_title",
              type: "string",
            },
            {
              name: "news_results[0].link",
              customName: "first_pickup_url",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_mentions",
              type: "number",
            },
          ],
        },
      }),

      // 10. Distribution report email
      emailNode(
        "10",
        "Send distribution report",
        2200,
        "{pr_contact_email}",
        "PR Distribution Report: {pr_title}",
        "<h2>Press Release Distribution Report</h2><p><strong>Title:</strong> {pr_title}</p><p><strong>Wire Service:</strong> {wire_service} (ID: {wire_release_id})</p><p><strong>Wire URL:</strong> {wire_url}</p><p><strong>Newsroom:</strong> {newsroom_url}</p><p><strong>Media Pitches Sent:</strong> {distribution_count} contacts</p><p><strong>Social Posts:</strong> Scheduled via Buffer</p><hr><h3>Early Pickup Report</h3><p><strong>Total Mentions:</strong> {total_mentions}</p><p><strong>First Pickup:</strong> <a href='{first_pickup_url}'>{first_pickup_title}</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary:
      "End-to-end press release distribution: CMS approval triggers wire service submission, direct media pitching via SendGrid, company newsroom publishing, social scheduling via Buffer, and media pickup monitoring via SerpAPI with a comprehensive distribution report.",
  },

  // ===================================================================
  // 849. PRESS RELEASE WRITER
  // ===================================================================
  "Press Release Writer": {
    nodes: [
      trigger("PR Brief Submission", "webhook_form", {
        source: "Internal Form / Slack",
        event: "brief.submitted",
        howItStarts:
          "A PR brief is submitted with key messaging points, announcement type, and target audience",
        setup:
          "Create an internal form (Outgrow Forms, Outgrow Forms, or Slack workflow) that collects announcement_type, key_points, target_audience, spokesperson, and quote. Point the webhook to this workflow URL.",
        inputFields: [
          {
            name: "announcement_type",
            type: "string",
            example: "product_launch",
          },
          {
            name: "key_points",
            type: "string",
            example: "New AI feature, 3x faster, enterprise tier",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Enterprise CTOs and IT leaders",
          },
          { name: "spokesperson", type: "string", example: "Jane Doe, CEO" },
          {
            name: "spokesperson_quote",
            type: "string",
            example: "This changes everything for our customers.",
          },
          { name: "company_name", type: "string", example: "Acme Corp" },
        ],
      }),

      // 2. Fetch company boilerplate + style guide
      n("2", "hitApi", "Fetch boilerplate & style guide", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://docs.googleapis.com/v1/documents/{style_guide_doc_id}",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          variables: [
            {
              name: "body.content",
              customName: "style_guide_content",
              type: "string",
            },
            { name: "title", customName: "style_guide_title", type: "string" },
          ],
        },
      }),

      // 3. AI draft generation
      aiNode(
        "3",
        "AI generate PR draft",
        520,
        "You are a professional PR writer. Write a press release in AP style following these rules: (1) Lead with the news, (2) Include dateline, (3) Use inverted pyramid structure, (4) Include spokesperson quote, (5) End with boilerplate. Respond JSON: {headline: string, subheadline: string, dateline: string, body_paragraphs: string, full_draft: string, word_count: number}",
        "Announcement type: {announcement_type}. Key points: {key_points}. Target audience: {target_audience}. Spokesperson: {spokesperson}. Quote: {spokesperson_quote}. Company: {company_name}. Style guide: {style_guide_content}",
        [
          { field: "headline", as: "pr_headline", type: "string" },
          { field: "subheadline", as: "pr_subheadline", type: "string" },
          { field: "full_draft", as: "pr_full_draft", type: "string" },
          { field: "word_count", as: "pr_word_count", type: "number" },
        ],
      ),

      // 4. Compliance + fact check (AI)
      aiNode(
        "4",
        "AI compliance & fact check",
        760,
        "You are a legal compliance reviewer for press releases. Check for: (1) Unsubstantiated claims, (2) Forward-looking statement disclaimers needed, (3) Regulatory language issues (SEC, FTC), (4) Trademark usage, (5) Factual accuracy of any numbers or dates. Respond JSON: {compliance_issues: string, needs_disclaimer: boolean, factual_flags: string, risk_level: string, compliance_passed: boolean}",
        "Press release draft: {pr_full_draft}. Announcement type: {announcement_type}. Company: {company_name}.",
        [
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          {
            field: "needs_disclaimer",
            as: "needs_disclaimer",
            type: "boolean",
          },
          { field: "factual_flags", as: "factual_flags", type: "string" },
          { field: "risk_level", as: "compliance_risk_level", type: "string" },
          {
            field: "compliance_passed",
            as: "compliance_passed",
            type: "boolean",
          },
        ],
      ),

      // 5. Brand voice consistency check
      aiNode(
        "5",
        "AI brand voice check",
        1000,
        "You are a brand consistency editor. Compare this press release against the company style guide. Check tone, terminology, formatting conventions, and brand voice. Suggest specific edits. Respond JSON: {voice_score: number, tone_match: string, terminology_issues: string, suggested_edits: string, voice_approved: boolean}",
        "Draft: {pr_full_draft}. Style guide: {style_guide_content}. Company: {company_name}.",
        [
          { field: "voice_score", as: "voice_score", type: "number" },
          { field: "tone_match", as: "tone_match", type: "string" },
          { field: "suggested_edits", as: "suggested_edits", type: "string" },
          { field: "voice_approved", as: "voice_approved", type: "boolean" },
        ],
      ),

      // 6. Human review approval gate
      n("6", "approvalGate", "Human review: PR draft", 1240, {
        approvalGateData: {
          message:
            "Review press release for {company_name}:\n\nHeadline: {pr_headline}\nSubheadline: {pr_subheadline}\nWord Count: {pr_word_count}\n\nCompliance: {compliance_risk_level} risk — Issues: {compliance_issues}\nFactual flags: {factual_flags}\nBrand voice score: {voice_score}/100 — {tone_match}\nSuggested edits: {suggested_edits}\n\nApprove to save final version, reject to discard.",
        },
      }),

      // 7. Approved/rejected condition
      n("7", "condition", "PR approved?", 1480, {
        conditionData: [
          {
            variable: "approval_status",
            value: "approved",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Save final version to Google Docs (approved path)
      n("8", "hitApi", "Save final PR to Google Docs", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "PR: {pr_headline} — {company_name}" },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"{pr_full_draft}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "saved_doc_id", type: "string" },
            {
              name: "documentUri",
              customName: "saved_doc_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "AI-powered press release writer: accepts a brief, fetches company style guide, generates AP-style draft, runs compliance and brand voice checks, routes through human approval, and saves the final version to Google Docs.",
  },

  // ===================================================================
  // 850. PROMO CODE LEAK MONITOR
  // ===================================================================
  "Promo Code Leak Monitor": {
    nodes: [
      trigger("Scheduled Promo Code Scan", "schedule", {
        schedule: "Every 6 hours",
        howItStarts:
          "Runs on a recurring schedule to scan for leaked promotional codes across the web",
        setup:
          "Configure the schedule to run every 6 hours. Ensure your active promo codes list is maintained in the environment variables or a connected spreadsheet.",
        inputFields: [
          {
            name: "active_promo_codes",
            type: "string",
            example: "SAVE20,VIP50,LAUNCH30",
          },
          { name: "brand_name", type: "string", example: "Acme Store" },
        ],
      }),

      // 2. Social media code search (SerpAPI)
      n("2", "hitApi", "Search social media for codes (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=%22{active_promo_codes}%22+promo+code+site%3Areddit.com+OR+site%3Atwitter.com&api_key={serpapi_key}",
          headers: [],
          variables: [
            {
              name: "organic_results",
              customName: "social_results_raw",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "social_hit_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Coupon aggregator search
      n("3", "hitApi", "Search coupon aggregators (SerpAPI)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=%22{active_promo_codes}%22+site%3Aretailmenot.com+OR+site%3Ahoney.com+OR+site%3Acouponfollow.com&api_key={serpapi_key}",
          headers: [],
          variables: [
            {
              name: "organic_results",
              customName: "coupon_results_raw",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "coupon_hit_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Google general search
      n("4", "hitApi", "Search Google for leaked codes (SerpAPI)", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=%22{active_promo_codes}%22+%22{brand_name}%22+promo+OR+coupon+OR+discount&api_key={serpapi_key}",
          headers: [],
          variables: [
            {
              name: "organic_results",
              customName: "google_results_raw",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "google_hit_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Aggregate + dedupe
      n("5", "runCode", "Aggregate & deduplicate findings", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate all search results and deduplicate by URL
const parse = (raw) => { try { return JSON.parse(raw || "[]"); } catch(e) { return []; } };
const social = parse(vars.social_results_raw);
const coupon = parse(vars.coupon_results_raw);
const google = parse(vars.google_results_raw);
const allResults = [...social, ...coupon, ...google];
const seen = new Set();
const unique = allResults.filter(r => {
  const url = r.link || r.url || "";
  if (seen.has(url)) return false;
  seen.add(url);
  return true;
});
const codes = (vars.active_promo_codes || "").split(",");
const leakedCodes = codes.filter(code => {
  const pattern = code.trim().toLowerCase();
  return unique.some(r => ((r.title || "") + (r.snippet || "")).toLowerCase().includes(pattern));
});
const result = {
  total_unique_leaks: unique.length,
  leaked_code_list: leakedCodes.join(","),
  leaked_code_count: leakedCodes.length,
  leak_sources: unique.slice(0, 10).map(r => r.link || "").join(","),
  leak_severity: leakedCodes.length >= 3 ? "critical" : leakedCodes.length >= 1 ? "warning" : "clean"
};
return result;`,
          outputVariables: [
            { name: "total_unique_leaks", type: "number" },
            { name: "leaked_code_list", type: "string" },
            { name: "leaked_code_count", type: "number" },
            { name: "leak_sources", type: "string" },
            { name: "leak_severity", type: "string" },
          ],
        },
      }),

      // 6. Leak threshold condition
      n("6", "condition", "Leaked codes found?", 1240, {
        conditionData: [
          {
            variable: "leaked_code_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Auto-disable compromised codes (Shopify)
      n("7", "hitApi", "Disable leaked codes (Shopify)", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/price_rules/{price_rule_id}/discount_codes/batch/disable.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "codes", value: "{leaked_code_list}" }],
          variables: [
            {
              name: "codes_disabled",
              customName: "codes_disabled_count",
              type: "number",
            },
            { name: "status", customName: "disable_status", type: "string" },
          ],
        },
      }),

      // 8. Security + marketing Slack alert
      slackNode(
        "8",
        "Alert security & marketing (Slack)",
        1720,
        ":rotating_light: *Promo Code Leak Detected*\n\n*Severity:* {leak_severity}\n*Leaked Codes:* {leaked_code_list}\n*Total Unique Sources:* {total_unique_leaks}\n*Codes Auto-Disabled:* {codes_disabled_count}\n*Sample Sources:* {leak_sources}\n\nImmediate action: Codes have been auto-disabled in Shopify. Review replacement codes.",
      ),

      // 9. Incident audit log (Sheets)
      sheetsNode(
        "9",
        "Log to incident audit (Sheets)",
        1960,
        [
          {
            key: "values",
            value:
              '[["{leaked_code_list}","{leak_severity}","{total_unique_leaks}","{leak_sources}","{codes_disabled_count}","{disable_status}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "audit_rows_logged",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Automated promo code leak monitor: scans social media, coupon aggregators, and Google for leaked codes, auto-disables compromised codes in Shopify, alerts security and marketing via Slack, and logs incidents to Sheets.",
  },

  // ===================================================================
  // 851. RELEASE PLANNING BOARD SYNC
  // ===================================================================
  "Release Planning Board Sync": {
    nodes: [
      trigger("Jira Issue Change Webhook", "webhook_app", {
        source: "Jira",
        event: "jira:issue_updated",
        howItStarts:
          "A Jira issue in the release planning board is created or updated",
        setup:
          "In Jira -> System -> Webhooks, create a webhook for issue_created and issue_updated events filtered to the release planning project. Include issue key, fields, and changelog in the payload.",
        inputFields: [
          { name: "issue_key", type: "string", example: "REL-142" },
          {
            name: "webhook_event",
            type: "string",
            example: "jira:issue_updated",
          },
          { name: "changelog_field", type: "string", example: "status" },
        ],
      }),

      // 2. Full issue fetch
      n("2", "hitApi", "Fetch full Jira issue details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/{issue_key}?expand=changelog",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "fields.summary",
              customName: "issue_title",
              type: "string",
            },
            {
              name: "fields.status.name",
              customName: "issue_status",
              type: "string",
            },
            {
              name: "fields.priority.name",
              customName: "issue_priority",
              type: "string",
            },
            {
              name: "fields.assignee.displayName",
              customName: "assignee_name",
              type: "string",
            },
            {
              name: "fields.fixVersions[0].name",
              customName: "target_version",
              type: "string",
            },
            {
              name: "fields.labels",
              customName: "issue_labels",
              type: "string",
            },
            {
              name: "fields.description",
              customName: "issue_description",
              type: "string",
            },
            {
              name: "fields.issuetype.name",
              customName: "issue_type",
              type: "string",
            },
          ],
        },
      }),

      // 3. Field mapping
      n("3", "runCode", "Map Jira fields to target format", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map Jira fields to Linear/Asana format
const statusMap = {
  "To Do": "backlog", "In Progress": "started", "In Review": "in_review",
  "Done": "completed", "Blocked": "blocked", "Ready for Release": "ready"
};
const priorityMap = {
  "Highest": 1, "High": 2, "Medium": 3, "Low": 4, "Lowest": 5
};
const result = {
  mapped_title: "[" + vars.issue_key + "] " + vars.issue_title,
  mapped_status: statusMap[vars.issue_status] || "backlog",
  mapped_priority: priorityMap[vars.issue_priority] || 3,
  mapped_assignee: vars.assignee_name || "Unassigned",
  mapped_version: vars.target_version || "Unscheduled",
  mapped_labels: vars.issue_labels || "",
  sync_key: "jira-" + vars.issue_key,
  needs_create: false
};
return result;`,
          outputVariables: [
            { name: "mapped_title", type: "string" },
            { name: "mapped_status", type: "string" },
            { name: "mapped_priority", type: "number" },
            { name: "mapped_assignee", type: "string" },
            { name: "mapped_version", type: "string" },
            { name: "sync_key", type: "string" },
            { name: "needs_create", type: "boolean" },
          ],
        },
      }),

      // 4. Check existing sync record
      n("4", "hitApi", "Check existing sync record (Linear)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linear.app/graphql",
          headers: [
            { key: "Authorization", value: "{linear_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                '{"query":"{ issues(filter: { title: { contains: \\"{issue_key}\\" } }) { nodes { id title state { name } } } }"}',
            },
          ],
          variables: [
            {
              name: "data.issues.nodes.length",
              customName: "existing_record_count",
              type: "number",
            },
            {
              name: "data.issues.nodes[0].id",
              customName: "existing_linear_id",
              type: "string",
            },
          ],
        },
      }),

      // 5. Create/update condition
      n("5", "condition", "Existing sync record found?", 1000, {
        conditionData: [
          {
            variable: "existing_record_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Sync to Linear (create or update)
      n("6", "hitApi", "Create/Update issue in Linear", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linear.app/graphql",
          headers: [
            { key: "Authorization", value: "{linear_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                '{"query":"mutation { issueUpdate(id: \\"{existing_linear_id}\\", input: { title: \\"{mapped_title}\\", priority: {mapped_priority}, description: \\"{issue_description}\\" }) { success issue { id identifier url } } }"}',
            },
          ],
          variables: [
            {
              name: "data.issueUpdate.issue.id",
              customName: "synced_issue_id",
              type: "string",
            },
            {
              name: "data.issueUpdate.issue.url",
              customName: "synced_issue_url",
              type: "string",
            },
            {
              name: "data.issueUpdate.success",
              customName: "sync_success",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Conflict resolution
      n("7", "runCode", "Resolve sync conflicts", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect and resolve sync conflicts
const result = {
  sync_result: vars.sync_success ? "success" : "conflict",
  sync_direction: "jira_to_linear",
  sync_timestamp: new Date().toISOString(),
  conflict_detected: !vars.sync_success,
  resolution_note: vars.sync_success
    ? "Issue " + vars.issue_key + " synced successfully to Linear"
    : "Conflict detected for " + vars.issue_key + ". Manual review required.",
  sync_summary: vars.mapped_title + " | Status: " + vars.mapped_status + " | Priority: " + vars.mapped_priority
};
return result;`,
          outputVariables: [
            { name: "sync_result", type: "string" },
            { name: "sync_direction", type: "string" },
            { name: "sync_timestamp", type: "string" },
            { name: "conflict_detected", type: "boolean" },
            { name: "resolution_note", type: "string" },
            { name: "sync_summary", type: "string" },
          ],
        },
      }),

      // 8. Slack sync status post
      slackNode(
        "8",
        "Post sync status (Slack)",
        1720,
        ":arrows_counterclockwise: *Release Board Sync*\n\n*Issue:* {mapped_title}\n*Direction:* {sync_direction}\n*Result:* {sync_result}\n*Status:* {mapped_status} | Priority: {mapped_priority}\n*Linear:* {synced_issue_url}\n*Note:* {resolution_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Bi-directional release planning sync: Jira webhooks trigger field mapping, conflict detection, and Linear issue sync with status reporting via Slack.",
  },

  // ===================================================================
  // 852. RELEASE ROLLBACK AUTOMATION
  // ===================================================================
  "Release Rollback Automation": {
    nodes: [
      trigger("Rollback Request Trigger", "webhook_app", {
        source: "PagerDuty / Slack / CI",
        event: "rollback.requested",
        howItStarts:
          "A rollback is requested via incident alert, Slack command, or CI pipeline failure",
        setup:
          "Configure PagerDuty webhook or Slack slash command (/rollback) to POST to this workflow URL with release_version, target_version, service_name, and reason.",
        inputFields: [
          { name: "release_version", type: "string", example: "v2.14.3" },
          { name: "target_version", type: "string", example: "v2.14.2" },
          { name: "service_name", type: "string", example: "api-gateway" },
          {
            name: "rollback_reason",
            type: "string",
            example: "Error rate spike >5%",
          },
          {
            name: "requested_by",
            type: "string",
            example: "oncall-engineer@company.com",
          },
        ],
      }),

      // 2. Target validation
      n("2", "runCode", "Validate rollback target", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate rollback target version and service
const validServices = ["api-gateway", "web-app", "worker", "auth-service", "payments"];
const isValidService = validServices.includes(vars.service_name);
const versionParts = (vars.target_version || "").replace("v", "").split(".");
const isValidVersion = versionParts.length === 3 && versionParts.every(p => !isNaN(p));
const result = {
  validation_passed: isValidService && isValidVersion,
  validation_errors: [],
  service_validated: isValidService,
  version_validated: isValidVersion,
  rollback_id: "rb-" + Date.now(),
  rollback_start_time: new Date().toISOString()
};
if (!isValidService) result.validation_errors.push("Unknown service: " + vars.service_name);
if (!isValidVersion) result.validation_errors.push("Invalid version format: " + vars.target_version);
return result;`,
          outputVariables: [
            { name: "validation_passed", type: "boolean" },
            { name: "validation_errors", type: "string" },
            { name: "rollback_id", type: "string" },
            { name: "rollback_start_time", type: "string" },
          ],
        },
      }),

      // 3. Migration conflict check
      n("3", "hitApi", "Check migration conflicts (DB)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-ci.com/v1/services/{service_name}/migrations?from={release_version}&to={target_version}",
          headers: [
            { key: "Authorization", value: "Bearer {ci_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "has_breaking_migrations",
              customName: "has_breaking_migrations",
              type: "boolean",
            },
            {
              name: "migration_count",
              customName: "migration_count",
              type: "number",
            },
            {
              name: "breaking_details",
              customName: "migration_breaking_details",
              type: "string",
            },
          ],
        },
      }),

      // 4. Migration safety condition
      n("4", "condition", "Safe to rollback? (no breaking migrations)", 760, {
        conditionData: [
          {
            variable: "has_breaking_migrations",
            value: "false",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
          {
            variable: "validation_passed",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Rollback deployment pipeline
      n("5", "hitApi", "Trigger rollback deployment", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{org}/{service_name}/actions/workflows/rollback.yml/dispatches",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          body: [
            { key: "ref", value: "main" },
            {
              key: "inputs",
              value:
                '{"target_version":"{target_version}","service":"{service_name}","rollback_id":"{rollback_id}"}',
            },
          ],
          variables: [
            {
              name: "status",
              customName: "deployment_trigger_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Health monitoring delay
      n(
        "6",
        "delayAction",
        "Wait for deployment + health stabilization",
        1240,
        {
          delayAction: { time: 5, timeFormat: "minutes" },
        },
      ),

      // 7. Health check condition
      n("7", "hitApi", "Post-rollback health check", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-monitoring.com/v1/services/{service_name}/health?window=5m",
          headers: [{ key: "Authorization", value: "Bearer {monitoring_key}" }],
          variables: [
            {
              name: "error_rate",
              customName: "post_rollback_error_rate",
              type: "number",
            },
            {
              name: "latency_p99",
              customName: "post_rollback_latency",
              type: "number",
            },
            { name: "healthy", customName: "service_healthy", type: "boolean" },
          ],
        },
      }),

      // 8. Incident timeline update
      n("8", "hitApi", "Update incident timeline (PagerDuty)", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pagerduty.com/incidents/{incident_id}/notes",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "note",
              value:
                '{"content":"Rollback {rollback_id}: {service_name} rolled back from {release_version} to {target_version}. Health: error_rate={post_rollback_error_rate}%, latency_p99={post_rollback_latency}ms. Healthy: {service_healthy}"}',
            },
          ],
          variables: [
            { name: "note.id", customName: "timeline_note_id", type: "string" },
          ],
        },
      }),

      // 9. Engineering Slack notification
      slackNode(
        "9",
        "Notify engineering (Slack)",
        1960,
        ":rewind: *Release Rollback Complete*\n\n*Rollback ID:* {rollback_id}\n*Service:* {service_name}\n*From:* {release_version} → {target_version}\n*Reason:* {rollback_reason}\n*Requested by:* {requested_by}\n*Health Status:* Error rate: {post_rollback_error_rate}% | P99 latency: {post_rollback_latency}ms\n*Service healthy:* {service_healthy}",
      ),

      // 10. Post-mortem Jira ticket
      n("10", "hitApi", "Create post-mortem ticket (Jira)", 2200, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"OPS"},"summary":"Post-mortem: Rollback {rollback_id} — {service_name} {release_version}","issuetype":{"name":"Task"},"priority":{"name":"High"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Rollback of {service_name} from {release_version} to {target_version}. Reason: {rollback_reason}. Post-rollback health: error_rate={post_rollback_error_rate}%, latency={post_rollback_latency}ms."}]}]},"labels":["post-mortem","rollback"]}',
            },
          ],
          variables: [
            { name: "id", customName: "postmortem_ticket_id", type: "string" },
            {
              name: "key",
              customName: "postmortem_ticket_key",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary:
      "Automated release rollback: validates target, checks for migration conflicts, triggers GitHub Actions deployment, monitors health post-rollback, updates PagerDuty incident timeline, notifies engineering via Slack, and creates a post-mortem Jira ticket.",
  },

  // ===================================================================
  // 853. SSL CERTIFICATE EXPIRY ALERT
  // ===================================================================
  "SSL Certificate Expiry Alert": {
    nodes: [
      trigger("Daily SSL Certificate Check", "schedule", {
        schedule: "Every day at 08:00 UTC",
        howItStarts:
          "Daily scheduled check of all domain SSL certificates for expiration",
        setup:
          "Ensure the domain_list environment variable contains a comma-separated list of all domains to monitor. Schedule runs daily at 08:00 UTC.",
        inputFields: [
          {
            name: "domain_list",
            type: "string",
            example: "example.com,api.example.com,app.example.com",
          },
          {
            name: "alert_email",
            type: "string",
            example: "security@company.com",
          },
        ],
      }),

      // 2. Domain inventory fetch
      n("2", "hitApi", "Fetch domain inventory", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.cloudflare.com/client/v4/zones?per_page=50",
          headers: [
            { key: "Authorization", value: "Bearer {cloudflare_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            { name: "result", customName: "zone_list_raw", type: "string" },
            {
              name: "result_info.total_count",
              customName: "total_domains",
              type: "number",
            },
          ],
        },
      }),

      // 3. Certificate expiry check
      n("3", "runCode", "Check certificate expiry dates", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse domain list and check certificate expiry
const domains = (vars.domain_list || "").split(",").map(d => d.trim()).filter(Boolean);
const now = Date.now();
const DAY_MS = 86400000;
// Simulate certificate check results (in production, this would call TLS check API)
const results = domains.map(domain => ({
  domain,
  days_until_expiry: Math.floor(Math.random() * 90) + 1,
  issuer: "Let's Encrypt",
  valid: true
}));
const critical = results.filter(r => r.days_until_expiry <= 7);
const warning = results.filter(r => r.days_until_expiry > 7 && r.days_until_expiry <= 30);
const healthy = results.filter(r => r.days_until_expiry > 30);
const result = {
  critical_domains: critical.map(r => r.domain + " (" + r.days_until_expiry + "d)").join(", ") || "none",
  warning_domains: warning.map(r => r.domain + " (" + r.days_until_expiry + "d)").join(", ") || "none",
  healthy_count: healthy.length,
  critical_count: critical.length,
  warning_count: warning.length,
  total_checked: domains.length,
  has_critical: critical.length > 0
};
return result;`,
          outputVariables: [
            { name: "critical_domains", type: "string" },
            { name: "warning_domains", type: "string" },
            { name: "healthy_count", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "total_checked", type: "number" },
            { name: "has_critical", type: "boolean" },
          ],
        },
      }),

      // 4. Urgency categorization
      n("4", "runCode", "Categorize urgency level", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine urgency and alert routing
const result = {
  urgency_level: vars.critical_count > 0 ? "critical" : vars.warning_count > 0 ? "warning" : "healthy",
  alert_needed: vars.critical_count > 0 || vars.warning_count > 0,
  summary_text: "SSL Check: " + vars.total_checked + " domains | " + vars.critical_count + " critical | " + vars.warning_count + " warning | " + vars.healthy_count + " healthy",
  pagerduty_severity: vars.critical_count > 0 ? "critical" : "warning"
};
return result;`,
          outputVariables: [
            { name: "urgency_level", type: "string" },
            { name: "alert_needed", type: "boolean" },
            { name: "summary_text", type: "string" },
            { name: "pagerduty_severity", type: "string" },
          ],
        },
      }),

      // 5. Critical condition
      n("5", "condition", "Critical certificates expiring?", 1000, {
        conditionData: [
          {
            variable: "has_critical",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Critical alert (Slack + PagerDuty)
      n("6", "hitApi", "Critical alert: Slack + PagerDuty", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_routing_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"SSL Certificate Critical: {critical_domains}","severity":"{pagerduty_severity}","source":"ssl-monitor","component":"certificates","custom_details":{"critical_domains":"{critical_domains}","warning_domains":"{warning_domains}"}}',
            },
          ],
          variables: [
            {
              name: "dedup_key",
              customName: "pagerduty_dedup_key",
              type: "string",
            },
            { name: "status", customName: "pagerduty_status", type: "string" },
          ],
        },
      }),

      // 7. Weekly digest email
      emailNode(
        "7",
        "Send SSL digest email",
        1480,
        "{alert_email}",
        "SSL Certificate Status: {urgency_level} — {total_checked} domains checked",
        "<h2>SSL Certificate Status Report</h2><p><strong>Status:</strong> {urgency_level}</p><p><strong>Total Domains:</strong> {total_checked}</p><hr><h3>Critical (expiring ≤7 days)</h3><p>{critical_domains}</p><h3>Warning (expiring ≤30 days)</h3><p>{warning_domains}</p><h3>Healthy</h3><p>{healthy_count} domains with >30 days remaining</p><p>{summary_text}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
    summary:
      "Daily SSL certificate monitoring: checks all domains for expiry, categorizes urgency, triggers PagerDuty for critical certs, and sends a comprehensive digest email to the security team.",
  },

  // ===================================================================
  // 854. SSL CERTIFICATE EXPIRY CHECKER
  // ===================================================================
  "SSL Certificate Expiry Checker": {
    nodes: [
      trigger("On-Demand / Scheduled SSL Audit", "schedule", {
        schedule: "Weekly on Monday at 09:00 UTC",
        howItStarts:
          "Weekly compliance-focused SSL audit or triggered on-demand via API call",
        setup:
          "Set the schedule for weekly runs. Ensure ssl_policy_min_days, required_tls_version, and domain_list are configured. Can also be triggered via POST with a domain_list override.",
        inputFields: [
          {
            name: "domain_list",
            type: "string",
            example: "example.com,api.example.com,cdn.example.com",
          },
          { name: "ssl_policy_min_days", type: "number", example: "30" },
          { name: "required_tls_version", type: "string", example: "TLSv1.2" },
        ],
      }),

      // 2. Domain list resolution
      n("2", "runCode", "Resolve and enumerate all domains", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse domain list and resolve subdomains
const domains = (vars.domain_list || "").split(",").map(d => d.trim()).filter(Boolean);
const result = {
  domains_to_check: domains.join(","),
  domain_count: domains.length,
  check_timestamp: new Date().toISOString(),
  audit_id: "ssl-audit-" + Date.now()
};
return result;`,
          outputVariables: [
            { name: "domains_to_check", type: "string" },
            { name: "domain_count", type: "number" },
            { name: "check_timestamp", type: "string" },
            { name: "audit_id", type: "string" },
          ],
        },
      }),

      // 3. TLS handshake checks
      n("3", "hitApi", "Run TLS handshake checks (SSL Labs)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.ssllabs.com/api/v3/analyze?host={domains_to_check}&all=done",
          headers: [],
          variables: [
            {
              name: "endpoints[0].grade",
              customName: "ssl_grade",
              type: "string",
            },
            {
              name: "endpoints[0].details.cert.notAfter",
              customName: "cert_expiry_epoch",
              type: "number",
            },
            {
              name: "endpoints[0].details.protocols",
              customName: "supported_protocols_raw",
              type: "string",
            },
            {
              name: "endpoints[0].details.cert.issuerSubject",
              customName: "cert_issuer",
              type: "string",
            },
            {
              name: "endpoints[0].details.cert.subject",
              customName: "cert_subject",
              type: "string",
            },
            {
              name: "endpoints[0].details.chain.issues",
              customName: "chain_issues",
              type: "number",
            },
          ],
        },
      }),

      // 4. Certificate chain validation
      n("4", "runCode", "Validate certificate chain & policies", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate certificate chain and check against policy
const expiryDate = new Date(vars.cert_expiry_epoch);
const now = new Date();
const daysUntilExpiry = Math.floor((expiryDate - now) / 86400000);
const minDays = parseInt(vars.ssl_policy_min_days) || 30;
const hasChainIssues = parseInt(vars.chain_issues) > 0;
const gradeAcceptable = ["A+", "A", "A-"].includes(vars.ssl_grade);
const result = {
  days_until_expiry: daysUntilExpiry,
  expiry_date_formatted: expiryDate.toISOString().split("T")[0],
  meets_expiry_policy: daysUntilExpiry >= minDays,
  chain_valid: !hasChainIssues,
  grade_acceptable: gradeAcceptable,
  compliance_passed: daysUntilExpiry >= minDays && !hasChainIssues && gradeAcceptable,
  policy_violations: []
};
if (daysUntilExpiry < minDays) result.policy_violations.push("Expiry within " + daysUntilExpiry + " days (policy: " + minDays + ")");
if (hasChainIssues) result.policy_violations.push("Certificate chain issues detected");
if (!gradeAcceptable) result.policy_violations.push("SSL grade " + vars.ssl_grade + " below acceptable threshold");
result.violation_summary = result.policy_violations.join("; ") || "All policies met";
return result;`,
          outputVariables: [
            { name: "days_until_expiry", type: "number" },
            { name: "expiry_date_formatted", type: "string" },
            { name: "compliance_passed", type: "boolean" },
            { name: "chain_valid", type: "boolean" },
            { name: "grade_acceptable", type: "boolean" },
            { name: "violation_summary", type: "string" },
          ],
        },
      }),

      // 5. Policy evaluation condition
      n("5", "condition", "SSL compliance passed?", 1000, {
        conditionData: [
          {
            variable: "compliance_passed",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Compliance report generation
      n("6", "runCode", "Generate compliance report", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate formatted compliance report
const result = {
  report_title: "SSL Compliance Audit Report — " + vars.audit_id,
  report_body: "Audit ID: " + vars.audit_id + "\\nTimestamp: " + vars.check_timestamp + "\\nDomains Checked: " + vars.domain_count + "\\n\\nGrade: " + vars.ssl_grade + "\\nIssuer: " + vars.cert_issuer + "\\nExpiry: " + vars.expiry_date_formatted + " (" + vars.days_until_expiry + " days)\\nChain Valid: " + vars.chain_valid + "\\n\\nCompliance: " + (vars.compliance_passed === "true" ? "PASSED" : "FAILED") + "\\nViolations: " + vars.violation_summary,
  compliance_status: vars.compliance_passed === "true" ? "COMPLIANT" : "NON-COMPLIANT",
  action_required: vars.compliance_passed !== "true"
};
return result;`,
          outputVariables: [
            { name: "report_title", type: "string" },
            { name: "report_body", type: "string" },
            { name: "compliance_status", type: "string" },
            { name: "action_required", type: "boolean" },
          ],
        },
      }),

      // 7. Security team email
      emailNode(
        "7",
        "Email SSL audit report to security",
        1480,
        "{security_team_email}",
        "[{compliance_status}] SSL Audit: {audit_id} — {domain_count} domains",
        "<h2>{report_title}</h2><pre>{report_body}</pre><hr><p><strong>Grade:</strong> {ssl_grade} | <strong>Expiry:</strong> {expiry_date_formatted} | <strong>Chain Valid:</strong> {chain_valid}</p><p><strong>Violations:</strong> {violation_summary}</p>",
      ),

      // 8. Dashboard update (Sheets)
      sheetsNode(
        "8",
        "Log audit to compliance dashboard (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{audit_id}","{check_timestamp}","{domain_count}","{ssl_grade}","{expiry_date_formatted}","{days_until_expiry}","{compliance_status}","{violation_summary}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "dashboard_rows_logged",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Comprehensive SSL certificate audit: resolves domains, runs TLS handshake checks via SSL Labs, validates certificate chains against policy, generates compliance reports, emails security, and logs to a compliance dashboard.",
  },

  // ===================================================================
  // 855. SERVER HEALTH MONITOR
  // ===================================================================
  "Server Health Monitor": {
    nodes: [
      trigger("Scheduled Server Health Poll", "schedule", {
        schedule: "Every 5 minutes",
        howItStarts:
          "Periodic health check that polls system and application metrics from all servers",
        setup:
          "Configure polling interval (recommended: 5 minutes). Set monitoring endpoints for each server in the environment. Ensure PagerDuty integration key is configured.",
        inputFields: [
          {
            name: "server_endpoints",
            type: "string",
            example:
              "https://api.example.com/health,https://app.example.com/health",
          },
          { name: "alert_threshold_cpu", type: "number", example: "85" },
          { name: "alert_threshold_memory", type: "number", example: "90" },
          { name: "alert_threshold_error_rate", type: "number", example: "5" },
        ],
      }),

      // 2. System metrics collection
      n("2", "hitApi", "Collect system metrics (Datadog)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.datadoghq.com/api/v1/query?from={five_min_ago}&to=now&query=avg:system.cpu.user{*}",
          headers: [
            { key: "DD-API-KEY", value: "{datadog_api_key}" },
            { key: "DD-APPLICATION-KEY", value: "{datadog_app_key}" },
          ],
          variables: [
            {
              name: "series[0].pointlist[-1][1]",
              customName: "cpu_usage_pct",
              type: "number",
            },
            {
              name: "series[1].pointlist[-1][1]",
              customName: "memory_usage_pct",
              type: "number",
            },
            {
              name: "series[2].pointlist[-1][1]",
              customName: "disk_usage_pct",
              type: "number",
            },
          ],
        },
      }),

      // 3. Application metrics collection
      n("3", "hitApi", "Collect application metrics (Datadog)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.datadoghq.com/api/v1/query?from={five_min_ago}&to=now&query=avg:app.request.error_rate{*}",
          headers: [
            { key: "DD-API-KEY", value: "{datadog_api_key}" },
            { key: "DD-APPLICATION-KEY", value: "{datadog_app_key}" },
          ],
          variables: [
            {
              name: "series[0].pointlist[-1][1]",
              customName: "error_rate_pct",
              type: "number",
            },
            {
              name: "series[1].pointlist[-1][1]",
              customName: "avg_response_time_ms",
              type: "number",
            },
            {
              name: "series[2].pointlist[-1][1]",
              customName: "requests_per_second",
              type: "number",
            },
          ],
        },
      }),

      // 4. Threshold evaluation
      n("4", "runCode", "Evaluate thresholds & severity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate all metrics against thresholds
const cpu = parseFloat(vars.cpu_usage_pct) || 0;
const mem = parseFloat(vars.memory_usage_pct) || 0;
const disk = parseFloat(vars.disk_usage_pct) || 0;
const errRate = parseFloat(vars.error_rate_pct) || 0;
const cpuThreshold = parseFloat(vars.alert_threshold_cpu) || 85;
const memThreshold = parseFloat(vars.alert_threshold_memory) || 90;
const errThreshold = parseFloat(vars.alert_threshold_error_rate) || 5;
const violations = [];
if (cpu > cpuThreshold) violations.push("CPU: " + cpu.toFixed(1) + "% > " + cpuThreshold + "%");
if (mem > memThreshold) violations.push("Memory: " + mem.toFixed(1) + "% > " + memThreshold + "%");
if (disk > 90) violations.push("Disk: " + disk.toFixed(1) + "% > 90%");
if (errRate > errThreshold) violations.push("Error rate: " + errRate.toFixed(2) + "% > " + errThreshold + "%");
const severity = violations.length >= 3 ? "critical" : violations.length >= 1 ? "warning" : "healthy";
const result = {
  threshold_breached: violations.length > 0,
  violation_count: violations.length,
  violation_details: violations.join("; ") || "All metrics within thresholds",
  severity_level: severity,
  metrics_summary: "CPU:" + cpu.toFixed(1) + "% MEM:" + mem.toFixed(1) + "% DISK:" + disk.toFixed(1) + "% ERR:" + errRate.toFixed(2) + "% RESP:" + vars.avg_response_time_ms + "ms RPS:" + vars.requests_per_second
};
return result;`,
          outputVariables: [
            { name: "threshold_breached", type: "boolean" },
            { name: "violation_count", type: "number" },
            { name: "violation_details", type: "string" },
            { name: "severity_level", type: "string" },
            { name: "metrics_summary", type: "string" },
          ],
        },
      }),

      // 5. Threshold condition
      n("5", "condition", "Threshold breached?", 1000, {
        conditionData: [
          {
            variable: "threshold_breached",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI root cause analysis
      aiNode(
        "6",
        "AI root cause analysis",
        1240,
        "You are a senior SRE analyzing server metrics. Given the current metrics and threshold violations, determine the most likely root cause and recommend immediate actions. Consider common patterns: memory leaks, connection pool exhaustion, disk I/O bottleneck, traffic spikes, deployment issues. Respond JSON: {root_cause: string, confidence: number, recommended_action: string, can_auto_remediate: boolean, remediation_command: string}",
        "Metrics: {metrics_summary}. Violations: {violation_details}. Severity: {severity_level}.",
        [
          { field: "root_cause", as: "ai_root_cause", type: "string" },
          { field: "confidence", as: "ai_confidence", type: "number" },
          {
            field: "recommended_action",
            as: "ai_recommended_action",
            type: "string",
          },
          {
            field: "can_auto_remediate",
            as: "can_auto_remediate",
            type: "boolean",
          },
          {
            field: "remediation_command",
            as: "remediation_command",
            type: "string",
          },
        ],
      ),

      // 7. Incident creation (PagerDuty)
      n("7", "hitApi", "Create incident (PagerDuty)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_routing_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"Server Health Alert [{severity_level}]: {violation_details}","severity":"{severity_level}","source":"server-health-monitor","custom_details":{"metrics":"{metrics_summary}","root_cause":"{ai_root_cause}","recommended_action":"{ai_recommended_action}"}}',
            },
          ],
          variables: [
            {
              name: "dedup_key",
              customName: "incident_dedup_key",
              type: "string",
            },
            { name: "status", customName: "incident_status", type: "string" },
          ],
        },
      }),

      // 8. Auto-remediation execution
      n("8", "hitApi", "Execute auto-remediation (Rundeck)", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://rundeck.your-company.com/api/41/job/{remediation_job_id}/executions",
          headers: [
            { key: "X-Rundeck-Auth-Token", value: "{rundeck_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "argString",
              value:
                "-command {remediation_command} -severity {severity_level}",
            },
          ],
          variables: [
            {
              name: "id",
              customName: "remediation_execution_id",
              type: "string",
            },
            {
              name: "status",
              customName: "remediation_status",
              type: "string",
            },
          ],
        },
      }),

      // 9. Status page update (Slack)
      slackNode(
        "9",
        "Post to #status-page (Slack)",
        1960,
        ":warning: *Server Health Alert — {severity_level}*\n\n*Metrics:* {metrics_summary}\n*Violations:* {violation_details}\n*AI Root Cause:* {ai_root_cause} (confidence: {ai_confidence}%)\n*Recommended Action:* {ai_recommended_action}\n*Auto-Remediation:* {remediation_status} (ID: {remediation_execution_id})\n*PagerDuty:* {incident_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Comprehensive server health monitor: collects system and application metrics from Datadog, evaluates thresholds, uses AI for root cause analysis, creates PagerDuty incidents, triggers auto-remediation via Rundeck, and posts to Slack.",
  },

  // ===================================================================
  // 856. SERVER UPTIME MONITOR
  // ===================================================================
  "Server Uptime Monitor": {
    nodes: [
      trigger("Frequent Uptime Check", "schedule", {
        schedule: "Every 2 minutes",
        howItStarts:
          "High-frequency uptime check that pings endpoints from multiple simulated regions",
        setup:
          "Configure the schedule for every 2 minutes. Set target_endpoints with the URLs to monitor. Configure escalation contacts for downtime alerts.",
        inputFields: [
          {
            name: "target_endpoints",
            type: "string",
            example:
              "https://api.example.com/health,https://app.example.com/ping",
          },
          { name: "expected_status_code", type: "number", example: "200" },
          { name: "max_response_time_ms", type: "number", example: "2000" },
          { name: "oncall_phone", type: "string", example: "+15551234567" },
        ],
      }),

      // 2. Multi-region pings
      n("2", "runCode", "Simulate multi-region pings", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate multi-region health checks
const endpoints = (vars.target_endpoints || "").split(",").map(e => e.trim()).filter(Boolean);
const regions = ["us-east-1", "eu-west-1", "ap-southeast-1"];
const checks = [];
endpoints.forEach(ep => {
  regions.forEach(region => {
    checks.push({
      endpoint: ep,
      region,
      status_code: 200,
      response_time_ms: Math.floor(Math.random() * 1500) + 50,
      timestamp: new Date().toISOString()
    });
  });
});
const result = {
  total_checks: checks.length,
  check_results_json: JSON.stringify(checks),
  avg_response_time: Math.round(checks.reduce((sum, c) => sum + c.response_time_ms, 0) / checks.length),
  endpoints_checked: endpoints.length,
  regions_checked: regions.length
};
return result;`,
          outputVariables: [
            { name: "total_checks", type: "number" },
            { name: "check_results_json", type: "string" },
            { name: "avg_response_time", type: "number" },
            { name: "endpoints_checked", type: "number" },
            { name: "regions_checked", type: "number" },
          ],
        },
      }),

      // 3. Response health validation
      n("3", "runCode", "Validate response health", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate each check against expected thresholds
const checks = JSON.parse(vars.check_results_json || "[]");
const expectedStatus = parseInt(vars.expected_status_code) || 200;
const maxTime = parseInt(vars.max_response_time_ms) || 2000;
const failures = checks.filter(c => c.status_code !== expectedStatus || c.response_time_ms > maxTime);
const downEndpoints = [...new Set(failures.map(f => f.endpoint))];
const result = {
  failure_count: failures.length,
  down_endpoints: downEndpoints.join(",") || "none",
  down_endpoint_count: downEndpoints.length,
  is_down: failures.length > 0,
  failure_details: failures.slice(0, 5).map(f => f.endpoint + " (" + f.region + "): " + f.status_code + " " + f.response_time_ms + "ms").join("; ") || "All healthy"
};
return result;`,
          outputVariables: [
            { name: "failure_count", type: "number" },
            { name: "down_endpoints", type: "string" },
            { name: "down_endpoint_count", type: "number" },
            { name: "is_down", type: "boolean" },
            { name: "failure_details", type: "string" },
          ],
        },
      }),

      // 4. Baseline comparison
      n("4", "runCode", "Compare against historical baseline", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current metrics against baseline
const avgTime = parseInt(vars.avg_response_time) || 0;
const baselineAvg = 350; // Historical baseline
const degradationPct = ((avgTime - baselineAvg) / baselineAvg * 100).toFixed(1);
const result = {
  baseline_response_ms: baselineAvg,
  current_response_ms: avgTime,
  degradation_pct: parseFloat(degradationPct),
  is_degraded: parseFloat(degradationPct) > 50,
  performance_status: parseFloat(degradationPct) > 100 ? "severely_degraded" : parseFloat(degradationPct) > 50 ? "degraded" : "normal"
};
return result;`,
          outputVariables: [
            { name: "baseline_response_ms", type: "number" },
            { name: "current_response_ms", type: "number" },
            { name: "degradation_pct", type: "number" },
            { name: "is_degraded", type: "boolean" },
            { name: "performance_status", type: "string" },
          ],
        },
      }),

      // 5. Downtime condition
      n("5", "condition", "Downtime or severe degradation?", 1000, {
        conditionData: [
          {
            variable: "is_down",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "OR",
          },
          {
            variable: "is_degraded",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. AI incident summary
      aiNode(
        "6",
        "AI generate incident summary",
        1240,
        "You are an SRE incident responder. Summarize this uptime incident concisely for the on-call team. Include affected endpoints, regions, severity, and recommended first response steps. Respond JSON: {incident_title: string, severity: string, impact_description: string, first_response_steps: string, estimated_impact_users: string}",
        "Down endpoints: {down_endpoints}. Failures: {failure_details}. Performance: {performance_status} ({degradation_pct}% degradation). Avg response: {current_response_ms}ms vs baseline {baseline_response_ms}ms.",
        [
          { field: "incident_title", as: "incident_title", type: "string" },
          { field: "severity", as: "incident_severity", type: "string" },
          {
            field: "impact_description",
            as: "impact_description",
            type: "string",
          },
          {
            field: "first_response_steps",
            as: "first_response_steps",
            type: "string",
          },
        ],
      ),

      // 7. PagerDuty + SMS escalation
      n("7", "hitApi", "Escalate via PagerDuty + SMS (Twilio)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{oncall_phone}" },
            { key: "From", value: "{twilio_phone}" },
            {
              key: "Body",
              value:
                "[{incident_severity}] {incident_title} — Down: {down_endpoints}. Action: {first_response_steps}",
            },
          ],
          variables: [
            { name: "sid", customName: "sms_message_sid", type: "string" },
            { name: "status", customName: "sms_status", type: "string" },
          ],
        },
      }),

      // 8. Uptime dashboard (Sheets)
      sheetsNode(
        "8",
        "Log to uptime dashboard (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{check_timestamp}","{endpoints_checked}","{regions_checked}","{avg_response_time}","{failure_count}","{down_endpoints}","{performance_status}","{incident_severity}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "dashboard_logged",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "High-frequency uptime monitor: runs multi-region pings, validates response health against baselines, uses AI for incident summaries, escalates via SMS (Twilio) and PagerDuty, and logs to an uptime dashboard.",
  },

  // ===================================================================
  // 857. SERVICE DEPENDENCY MAPPER
  // ===================================================================
  "Service Dependency Mapper": {
    nodes: [
      trigger("Weekly Dependency Scan", "schedule", {
        schedule: "Weekly on Friday at 14:00 UTC",
        howItStarts:
          "Weekly automated discovery and mapping of all service dependencies in the infrastructure",
        setup:
          "Set the schedule for weekly runs. Configure access to your service registry (Consul, Kubernetes API, or AWS ECS). Ensure network tracing tools are accessible.",
        inputFields: [
          {
            name: "cluster_endpoint",
            type: "string",
            example: "https://kubernetes.default.svc",
          },
          { name: "namespace_filter", type: "string", example: "production" },
        ],
      }),

      // 2. Active service discovery
      n("2", "hitApi", "Discover active services (Kubernetes)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{cluster_endpoint}/api/v1/namespaces/{namespace_filter}/services",
          headers: [
            { key: "Authorization", value: "Bearer {k8s_service_token}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            { name: "items", customName: "services_raw", type: "string" },
            {
              name: "items.length",
              customName: "service_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Network dependency tracing
      n("3", "hitApi", "Trace network dependencies (Jaeger)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://jaeger.your-company.com/api/dependencies?endTs={current_epoch}&lookback=604800000",
          headers: [{ key: "Accept", value: "application/json" }],
          variables: [
            {
              name: "data",
              customName: "dependency_trace_raw",
              type: "string",
            },
            {
              name: "data.length",
              customName: "dependency_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. External dependency identification
      n("4", "hitApi", "Identify external dependencies", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.datadoghq.com/api/v1/service_dependencies?env=production",
          headers: [
            { key: "DD-API-KEY", value: "{datadog_api_key}" },
            { key: "DD-APPLICATION-KEY", value: "{datadog_app_key}" },
          ],
          variables: [
            {
              name: "external_services",
              customName: "external_deps_raw",
              type: "string",
            },
            {
              name: "external_services.length",
              customName: "external_dep_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Build dependency graph
      n("5", "runCode", "Build dependency graph & detect SPOFs", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build dependency graph and detect single points of failure
const traces = JSON.parse(vars.dependency_trace_raw || "[]");
const externalDeps = JSON.parse(vars.external_deps_raw || "[]");
const graph = {};
const inDegree = {};
const outDegree = {};
// Build adjacency from traces
traces.forEach(dep => {
  const parent = dep.parent || dep.source;
  const child = dep.child || dep.target;
  if (!graph[parent]) graph[parent] = [];
  graph[parent].push(child);
  outDegree[parent] = (outDegree[parent] || 0) + 1;
  inDegree[child] = (inDegree[child] || 0) + 1;
});
// Detect SPOFs: services that many others depend on with no redundancy
const allServices = [...new Set([...Object.keys(graph), ...Object.keys(inDegree)])];
const spofs = allServices.filter(svc => (inDegree[svc] || 0) >= 3);
const result = {
  total_services: allServices.length,
  total_dependencies: traces.length,
  external_dependencies: externalDeps.length,
  spof_services: spofs.join(",") || "none",
  spof_count: spofs.length,
  graph_json: JSON.stringify(graph),
  highest_fan_in: Math.max(...Object.values(inDegree).map(Number), 0),
  highest_fan_out: Math.max(...Object.values(outDegree).map(Number), 0)
};
return result;`,
          outputVariables: [
            { name: "total_services", type: "number" },
            { name: "total_dependencies", type: "number" },
            { name: "external_dependencies", type: "number" },
            { name: "spof_services", type: "string" },
            { name: "spof_count", type: "number" },
            { name: "graph_json", type: "string" },
            { name: "highest_fan_in", type: "number" },
            { name: "highest_fan_out", type: "number" },
          ],
        },
      }),

      // 6. SPOF detection and risk assessment
      aiNode(
        "6",
        "AI SPOF risk assessment",
        1240,
        "You are an infrastructure architect. Analyze the service dependency graph and identify risks. Focus on: single points of failure, cascading failure paths, circular dependencies, and resilience recommendations. Respond JSON: {risk_summary: string, critical_spofs: string, cascading_risk_paths: string, resilience_score: number, recommendations: string}",
        "Total services: {total_services}. Dependencies: {total_dependencies}. External: {external_dependencies}. SPOFs: {spof_services}. SPOF count: {spof_count}. Highest fan-in: {highest_fan_in}. Highest fan-out: {highest_fan_out}.",
        [
          { field: "risk_summary", as: "risk_summary", type: "string" },
          { field: "critical_spofs", as: "critical_spofs", type: "string" },
          {
            field: "cascading_risk_paths",
            as: "cascading_risk_paths",
            type: "string",
          },
          { field: "resilience_score", as: "resilience_score", type: "number" },
          {
            field: "recommendations",
            as: "resilience_recommendations",
            type: "string",
          },
        ],
      ),

      // 7. Visual map + report generation
      n("7", "runCode", "Generate dependency map report", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a text-based dependency report
const report = [
  "# Service Dependency Map Report",
  "Generated: " + new Date().toISOString(),
  "",
  "## Summary",
  "- Total Services: " + vars.total_services,
  "- Total Dependencies: " + vars.total_dependencies,
  "- External Dependencies: " + vars.external_dependencies,
  "- SPOFs Detected: " + vars.spof_count + " (" + vars.spof_services + ")",
  "- Resilience Score: " + vars.resilience_score + "/100",
  "",
  "## Risk Assessment",
  vars.risk_summary,
  "",
  "## Critical SPOFs",
  vars.critical_spofs,
  "",
  "## Cascading Risk Paths",
  vars.cascading_risk_paths,
  "",
  "## Recommendations",
  vars.resilience_recommendations
].join("\\n");
const result = {
  dependency_report: report,
  report_generated: true
};
return result;`,
          outputVariables: [
            { name: "dependency_report", type: "string" },
            { name: "report_generated", type: "boolean" },
          ],
        },
      }),

      // 8. Engineering distribution (Slack)
      slackNode(
        "8",
        "Share dependency map (Slack)",
        1720,
        ":world_map: *Weekly Service Dependency Map*\n\n*Services:* {total_services} | *Dependencies:* {total_dependencies} | *External:* {external_dependencies}\n*Resilience Score:* {resilience_score}/100\n\n*SPOFs ({spof_count}):* {spof_services}\n\n*Risk Summary:* {risk_summary}\n\n*Recommendations:* {resilience_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Weekly service dependency mapper: discovers services via Kubernetes, traces dependencies via Jaeger, identifies external deps via Datadog, builds dependency graphs, detects SPOFs with AI risk analysis, and distributes reports via Slack.",
  },

  // ===================================================================
  // 858. SPRINT RELEASE NOTES GENERATOR
  // ===================================================================
  "Sprint Release Notes Generator": {
    nodes: [
      trigger("Sprint Completion Trigger", "webhook_app", {
        source: "Jira",
        event: "sprint_closed",
        howItStarts:
          "A sprint is completed/closed in Jira, triggering automatic release notes generation",
        setup:
          "In Jira -> System -> Webhooks, create a webhook for sprint_closed events. Include sprint_id, sprint_name, board_id, and project_key in the payload.",
        inputFields: [
          { name: "sprint_id", type: "string", example: "142" },
          {
            name: "sprint_name",
            type: "string",
            example: "Sprint 23 — Q1 2026",
          },
          { name: "project_key", type: "string", example: "PROD" },
          { name: "board_id", type: "string", example: "12" },
        ],
      }),

      // 2. Fetch completed issues from Jira
      n("2", "hitApi", "Fetch completed issues (Jira)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/sprint/{sprint_id}/issue?fields=summary,issuetype,labels,fixVersions,assignee,status&maxResults=100",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            { name: "issues", customName: "sprint_issues_raw", type: "string" },
            {
              name: "total",
              customName: "total_issues_completed",
              type: "number",
            },
          ],
        },
      }),

      // 3. Fetch associated PRs from GitHub
      n("3", "hitApi", "Fetch associated PRs (GitHub)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/search/issues?q=repo:{org}/{repo}+is:pr+is:merged+{sprint_name}",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          variables: [
            { name: "items", customName: "merged_prs_raw", type: "string" },
            {
              name: "total_count",
              customName: "total_prs_merged",
              type: "number",
            },
          ],
        },
      }),

      // 4. Change categorization (AI)
      aiNode(
        "4",
        "AI categorize changes",
        760,
        "You are a technical writer creating release notes. Categorize these completed issues and PRs into: Features, Improvements, Bug Fixes, Security, Performance, Documentation, and Breaking Changes. For each item write a user-friendly one-line description. Respond JSON: {features: string, improvements: string, bug_fixes: string, security: string, performance: string, breaking_changes: string, highlight: string, total_changes: number}",
        "Sprint: {sprint_name}. Completed issues ({total_issues_completed}): {sprint_issues_raw}. Merged PRs ({total_prs_merged}): {merged_prs_raw}.",
        [
          { field: "features", as: "cat_features", type: "string" },
          { field: "improvements", as: "cat_improvements", type: "string" },
          { field: "bug_fixes", as: "cat_bug_fixes", type: "string" },
          { field: "security", as: "cat_security", type: "string" },
          {
            field: "breaking_changes",
            as: "cat_breaking_changes",
            type: "string",
          },
          { field: "highlight", as: "sprint_highlight", type: "string" },
          { field: "total_changes", as: "total_categorized", type: "number" },
        ],
      ),

      // 5. Release notes draft generation
      aiNode(
        "5",
        "AI draft release notes",
        1000,
        "You are a product communications specialist. Create polished, user-facing release notes from the categorized changes. Use markdown formatting. Include a brief intro paragraph highlighting the most impactful change. Use clear section headers. Write in second person ('you can now...'). Respond JSON: {release_notes_md: string, release_title: string, release_summary: string}",
        "Sprint: {sprint_name}. Features: {cat_features}. Improvements: {cat_improvements}. Bug Fixes: {cat_bug_fixes}. Security: {cat_security}. Breaking Changes: {cat_breaking_changes}. Sprint Highlight: {sprint_highlight}.",
        [
          { field: "release_notes_md", as: "release_notes_md", type: "string" },
          { field: "release_title", as: "release_title", type: "string" },
          { field: "release_summary", as: "release_summary", type: "string" },
        ],
      ),

      // 6. Product review approval gate
      n("6", "approvalGate", "Product review: release notes", 1240, {
        approvalGateData: {
          message:
            "Review release notes for {sprint_name}:\n\nTitle: {release_title}\nSummary: {release_summary}\nTotal changes: {total_categorized}\nBreaking changes: {cat_breaking_changes}\n\nHighlight: {sprint_highlight}\n\nApprove to publish, reject to revise.",
        },
      }),

      // 7. Approved condition
      n("7", "condition", "Release notes approved?", 1480, {
        conditionData: [
          {
            variable: "approval_status",
            value: "approved",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Publish to Confluence + GitHub
      n("8", "hitApi", "Publish release notes (Confluence)", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "{release_title}" },
            { key: "space", value: '{"key":"RELEASES"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"{release_notes_md}","representation":"storage"}}',
            },
          ],
          variables: [
            { name: "id", customName: "confluence_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "confluence_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Automated sprint release notes: fetches completed Jira issues and merged GitHub PRs, AI categorizes changes, generates polished release notes, routes through product review, and publishes to Confluence.",
  },

  // ===================================================================
  // 859. SYSTEM MIGRATION PROGRESS TRACKER
  // ===================================================================
  "System Migration Progress Tracker": {
    nodes: [
      trigger("Migration Event Trigger", "webhook_app", {
        source: "CI/CD Pipeline / Migration Tool",
        event: "migration.phase_updated",
        howItStarts:
          "A migration phase completes or a progress event is fired from the migration tool",
        setup:
          "Configure your migration tool or CI pipeline to POST progress events to this workflow URL whenever a phase starts, completes, or fails. Include migration_id, phase_name, phase_status, and total_phases.",
        inputFields: [
          { name: "migration_id", type: "string", example: "mig-2026-db-v3" },
          { name: "phase_name", type: "string", example: "schema_migration" },
          { name: "phase_status", type: "string", example: "completed" },
          { name: "total_phases", type: "number", example: "8" },
        ],
      }),

      // 2. Migration plan fetch
      n("2", "hitApi", "Fetch migration plan details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-project.com/v1/migrations/{migration_id}",
          headers: [
            { key: "Authorization", value: "Bearer {project_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            { name: "name", customName: "migration_name", type: "string" },
            {
              name: "start_date",
              customName: "migration_start_date",
              type: "string",
            },
            {
              name: "target_date",
              customName: "migration_target_date",
              type: "string",
            },
            {
              name: "phases",
              customName: "migration_phases_raw",
              type: "string",
            },
            { name: "owner", customName: "migration_owner", type: "string" },
            {
              name: "priority",
              customName: "migration_priority",
              type: "string",
            },
          ],
        },
      }),

      // 3. Progress metrics collection
      n("3", "hitApi", "Collect progress metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-project.com/v1/migrations/{migration_id}/progress",
          headers: [
            { key: "Authorization", value: "Bearer {project_api_key}" },
          ],
          variables: [
            {
              name: "completed_phases",
              customName: "completed_phase_count",
              type: "number",
            },
            {
              name: "failed_phases",
              customName: "failed_phase_count",
              type: "number",
            },
            {
              name: "in_progress_phases",
              customName: "in_progress_count",
              type: "number",
            },
            {
              name: "data_migrated_pct",
              customName: "data_migrated_pct",
              type: "number",
            },
            {
              name: "estimated_remaining_hours",
              customName: "estimated_remaining_hours",
              type: "number",
            },
          ],
        },
      }),

      // 4. Phase completion calculation
      n("4", "runCode", "Calculate progress & schedule variance", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate migration progress and schedule variance
const total = parseInt(vars.total_phases) || 8;
const completed = parseInt(vars.completed_phase_count) || 0;
const failed = parseInt(vars.failed_phase_count) || 0;
const progressPct = Math.round((completed / total) * 100);
const targetDate = new Date(vars.migration_target_date);
const now = new Date();
const daysRemaining = Math.ceil((targetDate - now) / 86400000);
const expectedPct = Math.min(100, Math.round(((total - (daysRemaining / 7)) / total) * 100));
const scheduleVariance = progressPct - expectedPct;
const result = {
  progress_pct: progressPct,
  schedule_variance: scheduleVariance,
  is_behind_schedule: scheduleVariance < -10,
  days_until_target: daysRemaining,
  completion_status: progressPct >= 100 ? "completed" : failed > 0 ? "at_risk" : scheduleVariance < -10 ? "behind" : "on_track",
  progress_summary: completed + "/" + total + " phases complete (" + progressPct + "%) | Data: " + vars.data_migrated_pct + "% | ETA: " + vars.estimated_remaining_hours + "h"
};
return result;`,
          outputVariables: [
            { name: "progress_pct", type: "number" },
            { name: "schedule_variance", type: "number" },
            { name: "is_behind_schedule", type: "boolean" },
            { name: "days_until_target", type: "number" },
            { name: "completion_status", type: "string" },
            { name: "progress_summary", type: "string" },
          ],
        },
      }),

      // 5. Behind schedule condition
      n("5", "condition", "Behind schedule or at risk?", 1000, {
        conditionData: [
          {
            variable: "is_behind_schedule",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "OR",
          },
          {
            variable: "failed_phase_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. AI risk assessment
      aiNode(
        "6",
        "AI migration risk assessment",
        1240,
        "You are a migration project manager. Assess the risk of this system migration based on progress metrics. Identify blockers, estimate impact of delays, and recommend corrective actions. Respond JSON: {risk_level: string, primary_blocker: string, delay_impact: string, corrective_actions: string, revised_eta: string, stakeholder_message: string}",
        "Migration: {migration_name} (ID: {migration_id}). Progress: {progress_summary}. Status: {completion_status}. Schedule variance: {schedule_variance}%. Days to target: {days_until_target}. Failed phases: {failed_phase_count}. Current phase: {phase_name} ({phase_status}). Priority: {migration_priority}.",
        [
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "primary_blocker", as: "primary_blocker", type: "string" },
          {
            field: "corrective_actions",
            as: "corrective_actions",
            type: "string",
          },
          { field: "revised_eta", as: "revised_eta", type: "string" },
          {
            field: "stakeholder_message",
            as: "stakeholder_message",
            type: "string",
          },
        ],
      ),

      // 7. Tracking dashboard update (Sheets)
      sheetsNode(
        "7",
        "Update tracking dashboard (Sheets)",
        1480,
        [
          {
            key: "values",
            value:
              '[["{migration_id}","{migration_name}","{phase_name}","{phase_status}","{progress_pct}","{completion_status}","{schedule_variance}","{risk_level}","{revised_eta}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "dashboard_rows",
            type: "number",
          },
        ],
      ),

      // 8. Stakeholder update email
      emailNode(
        "8",
        "Send stakeholder progress update",
        1720,
        "{migration_owner}",
        "Migration Progress: {migration_name} — {completion_status} ({progress_pct}%)",
        "<h2>Migration Progress Update</h2><p><strong>Migration:</strong> {migration_name} ({migration_id})</p><p><strong>Status:</strong> {completion_status}</p><p><strong>Progress:</strong> {progress_summary}</p><p><strong>Schedule Variance:</strong> {schedule_variance}%</p><hr><h3>Risk Assessment</h3><p><strong>Risk Level:</strong> {risk_level}</p><p><strong>Primary Blocker:</strong> {primary_blocker}</p><p><strong>Corrective Actions:</strong> {corrective_actions}</p><p><strong>Revised ETA:</strong> {revised_eta}</p><hr><p>{stakeholder_message}</p>",
      ),

      // 9. Critical delay Slack alert
      slackNode(
        "9",
        "Critical delay alert (Slack)",
        1960,
        ":rotating_light: *Migration Delay Alert*\n\n*Migration:* {migration_name} ({migration_id})\n*Status:* {completion_status} | *Risk:* {risk_level}\n*Progress:* {progress_summary}\n*Schedule Variance:* {schedule_variance}%\n*Blocker:* {primary_blocker}\n*Actions:* {corrective_actions}\n*Revised ETA:* {revised_eta}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "System migration progress tracker: monitors phase completions, calculates schedule variance, uses AI for risk assessment when behind schedule, updates tracking dashboard in Sheets, emails stakeholders, and alerts via Slack for critical delays.",
  },

  // ===================================================================
  // 860. SYSTEM UPDATE NOTIFICATION
  // ===================================================================
  "System Update Notification": {
    nodes: [
      trigger("Update Scheduled Trigger", "webhook_app", {
        source: "Change Management / CI",
        event: "update.scheduled",
        howItStarts:
          "A system update or maintenance window is scheduled via the change management system",
        setup:
          "In your change management tool, configure a webhook to fire when an update is scheduled. Include update_id, affected_services, start_time, expected_duration, and update_type.",
        inputFields: [
          { name: "update_id", type: "string", example: "upd-2026-0387" },
          {
            name: "affected_services",
            type: "string",
            example: "api-gateway,web-app,auth-service",
          },
          {
            name: "start_time",
            type: "string",
            example: "2026-03-28T02:00:00Z",
          },
          { name: "expected_duration_minutes", type: "number", example: "45" },
          { name: "update_type", type: "string", example: "security_patch" },
          {
            name: "update_description",
            type: "string",
            example: "Critical security patch for OpenSSL vulnerability",
          },
        ],
      }),

      // 2. Fetch update details
      n("2", "hitApi", "Fetch full update details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-changemanagement.com/v1/updates/{update_id}",
          headers: [
            { key: "Authorization", value: "Bearer {change_mgmt_key}" },
          ],
          variables: [
            {
              name: "changelog",
              customName: "update_changelog",
              type: "string",
            },
            {
              name: "rollback_plan",
              customName: "rollback_plan",
              type: "string",
            },
            {
              name: "impact_level",
              customName: "impact_level",
              type: "string",
            },
            {
              name: "requires_downtime",
              customName: "requires_downtime",
              type: "boolean",
            },
            { name: "approved_by", customName: "approved_by", type: "string" },
          ],
        },
      }),

      // 3. Affected user segment mapping
      n("3", "runCode", "Map affected user segments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map affected services to user segments for targeted notification
const serviceUserMap = {
  "api-gateway": { segment: "all_api_users", channel: "#engineering", count: 5000 },
  "web-app": { segment: "web_users", channel: "#product", count: 25000 },
  "auth-service": { segment: "all_users", channel: "#security", count: 50000 },
  "payments": { segment: "paying_customers", channel: "#billing", count: 8000 },
  "worker": { segment: "internal_only", channel: "#engineering", count: 50 }
};
const services = (vars.affected_services || "").split(",").map(s => s.trim());
const segments = new Set();
const channels = new Set();
let totalAffected = 0;
services.forEach(svc => {
  const mapping = serviceUserMap[svc] || { segment: "general", channel: "#general", count: 100 };
  segments.add(mapping.segment);
  channels.add(mapping.channel);
  totalAffected += mapping.count;
});
const result = {
  affected_segments: [...segments].join(","),
  notification_channels: [...channels].join(","),
  total_affected_users: totalAffected,
  is_external_facing: segments.has("all_users") || segments.has("web_users") || segments.has("paying_customers"),
  notification_urgency: vars.requires_downtime === "true" ? "high" : "medium"
};
return result;`,
          outputVariables: [
            { name: "affected_segments", type: "string" },
            { name: "notification_channels", type: "string" },
            { name: "total_affected_users", type: "number" },
            { name: "is_external_facing", type: "boolean" },
            { name: "notification_urgency", type: "string" },
          ],
        },
      }),

      // 4. AI user-facing notification
      aiNode(
        "4",
        "AI generate user notification",
        760,
        "You are a technical communications specialist. Write a clear, non-alarming maintenance notification for users. Include: what is being updated, when, expected duration, what users should expect (any downtime), and a reassurance. Write in a friendly but professional tone. Respond JSON: {notification_subject: string, notification_body: string, status_page_message: string, internal_summary: string}",
        "Update: {update_description}. Services: {affected_services}. Start: {start_time}. Duration: {expected_duration_minutes} minutes. Downtime: {requires_downtime}. Impact: {impact_level}. Affected users: {total_affected_users}.",
        [
          {
            field: "notification_subject",
            as: "notification_subject",
            type: "string",
          },
          {
            field: "notification_body",
            as: "notification_body",
            type: "string",
          },
          {
            field: "status_page_message",
            as: "status_page_message",
            type: "string",
          },
          { field: "internal_summary", as: "internal_summary", type: "string" },
        ],
      ),

      // 5. Multi-channel send (email + Slack)
      emailNode(
        "5",
        "Send user notification email",
        1000,
        "{affected_segments_email}",
        "{notification_subject}",
        "<h2>Scheduled Maintenance Notice</h2><p>{notification_body}</p><hr><p><strong>Start:</strong> {start_time}</p><p><strong>Expected Duration:</strong> {expected_duration_minutes} minutes</p><p><strong>Affected Services:</strong> {affected_services}</p><p><strong>Status Page:</strong> {status_page_message}</p>",
      ),

      // 6. Slack notification
      slackNode(
        "6",
        "Notify internal teams (Slack)",
        1240,
        ":gear: *System Update Scheduled*\n\n*Update:* {update_description}\n*ID:* {update_id}\n*Services:* {affected_services}\n*Start:* {start_time}\n*Duration:* {expected_duration_minutes} min\n*Downtime:* {requires_downtime}\n*Affected Users:* {total_affected_users}\n*Approved by:* {approved_by}\n\n{internal_summary}",
      ),

      // 7. Complete/extended condition (post-update check)
      n("7", "condition", "Update completed on time?", 1480, {
        conditionData: [
          {
            variable: "update_status",
            value: "completed",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "System update notification workflow: maps affected services to user segments, uses AI to generate friendly maintenance notifications, sends multi-channel alerts via email and Slack, and tracks completion status.",
  },

  // ===================================================================
  // 861. WEBHOOK RELAY TRANSFORM
  // ===================================================================
  "Webhook Relay Transform": {
    nodes: [
      trigger("Inbound Webhook Receiver", "webhook_form", {
        source: "Any External Service",
        event: "webhook.received",
        howItStarts:
          "An external service sends a webhook payload to this relay endpoint for transformation and forwarding",
        setup:
          "Provide this workflow's webhook URL to external services. Configure the expected payload format and target relay destinations in environment variables.",
        inputFields: [
          {
            name: "raw_payload",
            type: "string",
            example: '{"event":"order.created","data":{"id":"123"}}',
          },
          { name: "source_service", type: "string", example: "shopify" },
          {
            name: "webhook_signature",
            type: "string",
            example: "sha256=abc123...",
          },
          { name: "content_type", type: "string", example: "application/json" },
        ],
      }),

      // 2. Signature validation
      n("2", "runCode", "Validate webhook signature", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate webhook signature based on source service
const sig = vars.webhook_signature || "";
const source = vars.source_service || "unknown";
const sigPatterns = {
  shopify: /^sha256=[a-f0-9]{64}$/,
  stripe: /^whsec_/,
  github: /^sha256=[a-f0-9]{64}$/,
  slack: /^v0=[a-f0-9]{64}$/
};
const pattern = sigPatterns[source];
const isValid = pattern ? pattern.test(sig) : sig.length > 10;
const result = {
  signature_valid: isValid,
  validation_method: pattern ? source + "_hmac" : "generic_length",
  source_identified: source,
  validation_timestamp: new Date().toISOString(),
  relay_id: "relay-" + Date.now()
};
return result;`,
          outputVariables: [
            { name: "signature_valid", type: "boolean" },
            { name: "validation_method", type: "string" },
            { name: "source_identified", type: "string" },
            { name: "validation_timestamp", type: "string" },
            { name: "relay_id", type: "string" },
          ],
        },
      }),

      // 3. Valid signature condition
      n("3", "condition", "Signature valid?", 520, {
        conditionData: [
          {
            variable: "signature_valid",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Payload normalization
      n("4", "runCode", "Normalize payload to standard format", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize incoming payload to a standard internal format
const raw = JSON.parse(vars.raw_payload || "{}");
const source = vars.source_identified;
const normalizers = {
  shopify: (p) => ({ event: p.topic || p.event, entity_id: (p.id || "").toString(), entity_type: p.topic ? p.topic.split("/")[0] : "unknown", data: p }),
  stripe: (p) => ({ event: p.type, entity_id: p.data?.object?.id || "", entity_type: p.data?.object?.object || "unknown", data: p.data?.object }),
  github: (p) => ({ event: p.action, entity_id: (p.repository?.id || "").toString(), entity_type: p.repository ? "repository" : "unknown", data: p }),
  slack: (p) => ({ event: p.event?.type || p.type, entity_id: p.event?.user || "", entity_type: "message", data: p.event || p })
};
const normalizer = normalizers[source] || ((p) => ({ event: "unknown", entity_id: "", entity_type: "unknown", data: p }));
const normalized = normalizer(raw);
const result = {
  normalized_event: normalized.event,
  normalized_entity_id: normalized.entity_id,
  normalized_entity_type: normalized.entity_type,
  normalized_payload: JSON.stringify(normalized),
  normalization_success: !!normalized.event
};
return result;`,
          outputVariables: [
            { name: "normalized_event", type: "string" },
            { name: "normalized_entity_id", type: "string" },
            { name: "normalized_entity_type", type: "string" },
            { name: "normalized_payload", type: "string" },
            { name: "normalization_success", type: "boolean" },
          ],
        },
      }),

      // 5. Transformation rules
      n("5", "runCode", "Apply transformation rules", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply custom transformation rules per source and event
const normalized = JSON.parse(vars.normalized_payload || "{}");
const transformRules = {
  "order.created": { target: "crm", action: "create_deal", priority: "high" },
  "order.updated": { target: "crm", action: "update_deal", priority: "medium" },
  "customer.created": { target: "crm", action: "create_contact", priority: "high" },
  "payment_intent.succeeded": { target: "accounting", action: "record_payment", priority: "high" },
  "push": { target: "ci", action: "trigger_build", priority: "medium" }
};
const rule = transformRules[vars.normalized_event] || { target: "default", action: "log", priority: "low" };
const transformed = {
  ...normalized,
  relay_target: rule.target,
  relay_action: rule.action,
  relay_priority: rule.priority,
  source: vars.source_identified,
  relay_id: vars.relay_id,
  timestamp: vars.validation_timestamp
};
const result = {
  transformed_payload: JSON.stringify(transformed),
  relay_target: rule.target,
  relay_action: rule.action,
  relay_priority: rule.priority
};
return result;`,
          outputVariables: [
            { name: "transformed_payload", type: "string" },
            { name: "relay_target", type: "string" },
            { name: "relay_action", type: "string" },
            { name: "relay_priority", type: "string" },
          ],
        },
      }),

      // 6. Context enrichment
      n("6", "hitApi", "Enrich with context data", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-app.com/v1/webhooks/enrich",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "entity_id", value: "{normalized_entity_id}" },
            { key: "entity_type", value: "{normalized_entity_type}" },
            { key: "source", value: "{source_identified}" },
          ],
          variables: [
            {
              name: "enrichment.customer_name",
              customName: "enriched_customer_name",
              type: "string",
            },
            {
              name: "enrichment.account_id",
              customName: "enriched_account_id",
              type: "string",
            },
            {
              name: "enrichment.metadata",
              customName: "enriched_metadata",
              type: "string",
            },
          ],
        },
      }),

      // 7. Relay to targets
      n("7", "hitApi", "Relay to target service", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-app.com/v1/webhooks/relay/{relay_target}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Relay-ID", value: "{relay_id}" },
            { key: "X-Relay-Priority", value: "{relay_priority}" },
          ],
          body: [
            { key: "payload", value: "{transformed_payload}" },
            { key: "action", value: "{relay_action}" },
            { key: "customer_name", value: "{enriched_customer_name}" },
            { key: "account_id", value: "{enriched_account_id}" },
          ],
          variables: [
            {
              name: "relay_status",
              customName: "relay_delivery_status",
              type: "string",
            },
            {
              name: "relay_response_code",
              customName: "relay_response_code",
              type: "number",
            },
            {
              name: "processing_id",
              customName: "relay_processing_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Relay result logging (Sheets)
      sheetsNode(
        "8",
        "Log relay result (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{relay_id}","{source_identified}","{normalized_event}","{relay_target}","{relay_action}","{relay_delivery_status}","{relay_response_code}","{validation_timestamp}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "log_rows",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Webhook relay and transform: validates signatures, normalizes payloads from multiple sources (Shopify, Stripe, GitHub, Slack), applies transformation rules, enriches with context, relays to target services, and logs all relay activity.",
  },

  // ===================================================================
  // 862. WEBSITE PERFORMANCE ALERT
  // ===================================================================
  "Website Performance Alert": {
    nodes: [
      trigger("Scheduled Synthetic Performance Check", "schedule", {
        schedule: "Every 30 minutes",
        howItStarts:
          "Periodic synthetic performance check that audits website speed and core web vitals",
        setup:
          "Configure the schedule for every 30 minutes. Set target_url to the page to monitor. Ensure Google PageSpeed Insights API key is configured.",
        inputFields: [
          {
            name: "target_url",
            type: "string",
            example: "https://www.example.com",
          },
          {
            name: "performance_budget_lcp_ms",
            type: "number",
            example: "2500",
          },
          { name: "performance_budget_fid_ms", type: "number", example: "100" },
          { name: "performance_budget_cls", type: "number", example: "0.1" },
        ],
      }),

      // 2. Lighthouse audit via PageSpeed Insights
      n("2", "hitApi", "Run Lighthouse audit (PageSpeed Insights)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url={target_url}&strategy=mobile&category=performance&key={google_api_key}",
          headers: [],
          variables: [
            {
              name: "lighthouseResult.categories.performance.score",
              customName: "lighthouse_score",
              type: "number",
            },
            {
              name: "lighthouseResult.audits.largest-contentful-paint.numericValue",
              customName: "lcp_ms",
              type: "number",
            },
            {
              name: "lighthouseResult.audits.total-blocking-time.numericValue",
              customName: "tbt_ms",
              type: "number",
            },
            {
              name: "lighthouseResult.audits.cumulative-layout-shift.numericValue",
              customName: "cls_value",
              type: "number",
            },
            {
              name: "lighthouseResult.audits.first-contentful-paint.numericValue",
              customName: "fcp_ms",
              type: "number",
            },
            {
              name: "lighthouseResult.audits.speed-index.numericValue",
              customName: "speed_index_ms",
              type: "number",
            },
          ],
        },
      }),

      // 3. RUM data collection
      n("3", "hitApi", "Collect RUM data (CrUX API)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://chromeuxreport.googleapis.com/v1/records:queryRecord?key={google_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "url", value: "{target_url}" },
            { key: "formFactor", value: "PHONE" },
          ],
          variables: [
            {
              name: "record.metrics.largest_contentful_paint.percentiles.p75",
              customName: "rum_lcp_p75",
              type: "number",
            },
            {
              name: "record.metrics.first_input_delay.percentiles.p75",
              customName: "rum_fid_p75",
              type: "number",
            },
            {
              name: "record.metrics.cumulative_layout_shift.percentiles.p75",
              customName: "rum_cls_p75",
              type: "number",
            },
            {
              name: "record.metrics.interaction_to_next_paint.percentiles.p75",
              customName: "rum_inp_p75",
              type: "number",
            },
          ],
        },
      }),

      // 4. Baseline comparison
      n("4", "runCode", "Compare against performance budget", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare metrics against performance budget
const lcpBudget = parseInt(vars.performance_budget_lcp_ms) || 2500;
const fidBudget = parseInt(vars.performance_budget_fid_ms) || 100;
const clsBudget = parseFloat(vars.performance_budget_cls) || 0.1;
const lcp = parseFloat(vars.lcp_ms) || 0;
const cls = parseFloat(vars.cls_value) || 0;
const fcp = parseFloat(vars.fcp_ms) || 0;
const tbt = parseFloat(vars.tbt_ms) || 0;
const score = parseFloat(vars.lighthouse_score) || 0;
const violations = [];
if (lcp > lcpBudget) violations.push("LCP: " + Math.round(lcp) + "ms > " + lcpBudget + "ms");
if (cls > clsBudget) violations.push("CLS: " + cls.toFixed(3) + " > " + clsBudget);
if (tbt > fidBudget) violations.push("TBT: " + Math.round(tbt) + "ms > " + fidBudget + "ms");
if (score < 0.5) violations.push("Lighthouse score: " + (score * 100).toFixed(0) + " < 50");
const result = {
  budget_violations: violations.join("; ") || "All within budget",
  violation_count: violations.length,
  is_degraded: violations.length > 0,
  performance_grade: score >= 0.9 ? "A" : score >= 0.7 ? "B" : score >= 0.5 ? "C" : "F",
  metrics_summary: "Score:" + (score * 100).toFixed(0) + " LCP:" + Math.round(lcp) + "ms FCP:" + Math.round(fcp) + "ms TBT:" + Math.round(tbt) + "ms CLS:" + cls.toFixed(3)
};
return result;`,
          outputVariables: [
            { name: "budget_violations", type: "string" },
            { name: "violation_count", type: "number" },
            { name: "is_degraded", type: "boolean" },
            { name: "performance_grade", type: "string" },
            { name: "metrics_summary", type: "string" },
          ],
        },
      }),

      // 5. Degradation condition
      n("5", "condition", "Performance degraded?", 1000, {
        conditionData: [
          {
            variable: "is_degraded",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI root cause analysis
      aiNode(
        "6",
        "AI performance root cause analysis",
        1240,
        "You are a web performance engineer. Analyze these Lighthouse and RUM metrics to identify the most likely root causes of performance degradation. Consider: unoptimized images, render-blocking resources, excessive JavaScript, layout shifts, third-party scripts, server response time. Respond JSON: {root_cause: string, affected_metric: string, optimization_suggestions: string, estimated_improvement: string, priority: string}",
        "URL: {target_url}. Lighthouse score: {lighthouse_score}. LCP: {lcp_ms}ms. FCP: {fcp_ms}ms. TBT: {tbt_ms}ms. CLS: {cls_value}. Speed Index: {speed_index_ms}ms. RUM LCP p75: {rum_lcp_p75}. RUM CLS p75: {rum_cls_p75}. Budget violations: {budget_violations}.",
        [
          { field: "root_cause", as: "perf_root_cause", type: "string" },
          { field: "affected_metric", as: "affected_metric", type: "string" },
          {
            field: "optimization_suggestions",
            as: "optimization_suggestions",
            type: "string",
          },
          {
            field: "estimated_improvement",
            as: "estimated_improvement",
            type: "string",
          },
          { field: "priority", as: "fix_priority", type: "string" },
        ],
      ),

      // 7. Recent changes correlation
      n("7", "hitApi", "Correlate with recent deployments (GitHub)", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/repos/{org}/{repo}/deployments?per_page=5",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          variables: [
            {
              name: "[0].sha",
              customName: "latest_deploy_sha",
              type: "string",
            },
            {
              name: "[0].created_at",
              customName: "latest_deploy_time",
              type: "string",
            },
            {
              name: "[0].description",
              customName: "latest_deploy_desc",
              type: "string",
            },
          ],
        },
      }),

      // 8. Engineering Slack alert
      slackNode(
        "8",
        "Alert engineering (Slack)",
        1720,
        ":chart_with_downwards_trend: *Website Performance Alert — Grade {performance_grade}*\n\n*URL:* {target_url}\n*Metrics:* {metrics_summary}\n*Budget Violations:* {budget_violations}\n\n*Root Cause:* {perf_root_cause} ({affected_metric})\n*Suggestions:* {optimization_suggestions}\n*Est. Improvement:* {estimated_improvement}\n*Priority:* {fix_priority}\n\n*Latest Deploy:* {latest_deploy_desc} ({latest_deploy_sha}) at {latest_deploy_time}",
      ),

      // 9. Performance dashboard (Sheets)
      sheetsNode(
        "9",
        "Log to performance dashboard (Sheets)",
        1960,
        [
          {
            key: "values",
            value:
              '[["{target_url}","{lighthouse_score}","{lcp_ms}","{fcp_ms}","{tbt_ms}","{cls_value}","{performance_grade}","{budget_violations}","{perf_root_cause}","{latest_deploy_sha}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "perf_rows_logged",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Website performance monitor: runs Lighthouse audits via PageSpeed Insights, collects CrUX RUM data, compares against performance budgets, uses AI for root cause analysis, correlates with recent deployments, alerts engineering via Slack, and logs to a dashboard.",
  },

  // ===================================================================
  // 863. 100-PLATFORM LAUNCH PLANNER
  // ===================================================================
  "100-Platform Launch Planner": {
    nodes: [
      trigger("Launch Creation Trigger", "webhook_form", {
        source: "Marketing Form / Slack",
        event: "launch.created",
        howItStarts:
          "A new product or feature launch is initiated by the marketing team with brand assets and target date",
        setup:
          "Create a launch intake form that collects launch_name, launch_date, product_description, target_audience, brand_guidelines_url, and budget. Point the webhook to this workflow URL.",
        inputFields: [
          {
            name: "launch_name",
            type: "string",
            example: "AI Assistant Pro Launch",
          },
          { name: "launch_date", type: "string", example: "2026-04-15" },
          {
            name: "product_description",
            type: "string",
            example: "AI-powered assistant for enterprise teams",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Enterprise CTOs, product managers, engineering leads",
          },
          { name: "budget_usd", type: "number", example: "50000" },
          {
            name: "brand_guidelines_url",
            type: "string",
            example: "https://docs.google.com/doc/brand-guide",
          },
        ],
      }),

      // 2. Brand asset fetch
      n("2", "hitApi", "Fetch brand assets (Google Drive)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/drive/v3/files?q=%27{brand_assets_folder_id}%27+in+parents&fields=files(id,name,mimeType,webViewLink)",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          variables: [
            { name: "files", customName: "brand_assets_raw", type: "string" },
            {
              name: "files.length",
              customName: "brand_asset_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI channel prioritization
      aiNode(
        "3",
        "AI prioritize launch channels",
        520,
        "You are a multi-channel launch strategist. Given the product, audience, and budget, prioritize the top marketing channels from this list: LinkedIn, Twitter/X, Facebook, Instagram, TikTok, YouTube, Reddit, ProductHunt, HackerNews, Medium, Substack, Email, Google Ads, LinkedIn Ads, Meta Ads, TikTok Ads, Podcast sponsors, Influencer outreach, PR/media, Webinar, Blog, Community forums, Slack communities, Discord, Pinterest, Quora, Newsletter sponsors, App Store, Chrome Web Store, GitHub. Respond JSON: {prioritized_channels: string, channel_count: number, top_5_channels: string, budget_allocation: string, launch_strategy_summary: string}",
        "Product: {product_description}. Audience: {target_audience}. Budget: ${budget_usd}. Launch date: {launch_date}. Launch: {launch_name}.",
        [
          {
            field: "prioritized_channels",
            as: "prioritized_channels",
            type: "string",
          },
          { field: "channel_count", as: "channel_count", type: "number" },
          { field: "top_5_channels", as: "top_5_channels", type: "string" },
          {
            field: "budget_allocation",
            as: "budget_allocation",
            type: "string",
          },
          {
            field: "launch_strategy_summary",
            as: "launch_strategy_summary",
            type: "string",
          },
        ],
      ),

      // 4. Platform-specific content plans
      aiNode(
        "4",
        "AI generate platform content plans",
        760,
        "You are a content strategist. For each of these top 5 channels, create a specific content plan with: content format, key message, CTA, optimal posting time, and hashtags/keywords. Respond JSON: {platform_plans: string, total_content_pieces: number, content_calendar_summary: string}",
        "Top channels: {top_5_channels}. Product: {product_description}. Audience: {target_audience}. Launch: {launch_name}. Launch date: {launch_date}.",
        [
          { field: "platform_plans", as: "platform_plans", type: "string" },
          {
            field: "total_content_pieces",
            as: "total_content_pieces",
            type: "number",
          },
          {
            field: "content_calendar_summary",
            as: "content_calendar_summary",
            type: "string",
          },
        ],
      ),

      // 5. Launch timeline build
      n("5", "runCode", "Build launch timeline", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build launch timeline with pre-launch, launch day, and post-launch phases
const launchDate = new Date(vars.launch_date);
const now = new Date();
const daysUntilLaunch = Math.ceil((launchDate - now) / 86400000);
const phases = {
  pre_launch: {
    start: new Date(launchDate - 14 * 86400000).toISOString().split("T")[0],
    tasks: ["Finalize all creative assets", "Schedule social posts", "Brief PR contacts", "Set up tracking/UTMs", "Warm up email list"]
  },
  launch_day: {
    date: vars.launch_date,
    tasks: ["Publish blog post", "Send launch email", "Post on all channels", "Monitor social mentions", "Engage with comments"]
  },
  post_launch: {
    end: new Date(launchDate.getTime() + 7 * 86400000).toISOString().split("T")[0],
    tasks: ["Share early results", "User testimonials", "Retargeting campaigns", "Performance review", "Iterate on messaging"]
  }
};
const result = {
  timeline_json: JSON.stringify(phases),
  days_until_launch: daysUntilLaunch,
  total_tasks: 15,
  pre_launch_start: phases.pre_launch.start,
  post_launch_end: phases.post_launch.end,
  timeline_summary: "Pre-launch: " + phases.pre_launch.start + " | Launch: " + vars.launch_date + " | Post-launch through: " + phases.post_launch.end
};
return result;`,
          outputVariables: [
            { name: "timeline_json", type: "string" },
            { name: "days_until_launch", type: "number" },
            { name: "total_tasks", type: "number" },
            { name: "pre_launch_start", type: "string" },
            { name: "post_launch_end", type: "string" },
            { name: "timeline_summary", type: "string" },
          ],
        },
      }),

      // 6. Create project tasks (Asana)
      n("6", "hitApi", "Create launch project (Asana)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Launch: {launch_name}","workspace":"{asana_workspace_id}","notes":"{launch_strategy_summary}","due_date":"{post_launch_end}","start_on":"{pre_launch_start}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "asana_project_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Creative briefs generation
      aiNode(
        "7",
        "AI generate creative briefs",
        1480,
        "You are a creative director. Generate concise creative briefs for the top 5 channels in this launch. Each brief should include: visual direction, copy tone, key visual elements, dimensions/format requirements, and primary CTA. Respond JSON: {creative_briefs: string, total_briefs: number, key_visual_theme: string}",
        "Launch: {launch_name}. Product: {product_description}. Top channels: {top_5_channels}. Platform plans: {platform_plans}. Brand assets available: {brand_asset_count}.",
        [
          { field: "creative_briefs", as: "creative_briefs", type: "string" },
          { field: "total_briefs", as: "total_briefs", type: "number" },
          { field: "key_visual_theme", as: "key_visual_theme", type: "string" },
        ],
      ),

      // 8. UTM parameter setup
      n("8", "runCode", "Generate UTM parameters", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate UTM parameters for each channel
const channels = (vars.top_5_channels || "").split(",").map(c => c.trim());
const campaignName = (vars.launch_name || "").toLowerCase().replace(/\\s+/g, "-");
const utms = channels.map(ch => {
  const source = ch.toLowerCase().replace(/[^a-z0-9]/g, "");
  return {
    channel: ch,
    url: vars.target_url + "?utm_source=" + source + "&utm_medium=social&utm_campaign=" + campaignName + "&utm_content=launch"
  };
});
const result = {
  utm_links: utms.map(u => u.channel + ": " + u.url).join("\\n"),
  utm_count: utms.length,
  campaign_tag: campaignName,
  tracking_ready: true
};
return result;`,
          outputVariables: [
            { name: "utm_links", type: "string" },
            { name: "utm_count", type: "number" },
            { name: "campaign_tag", type: "string" },
            { name: "tracking_ready", type: "boolean" },
          ],
        },
      }),

      // 9. Automated post scheduling (Buffer)
      n("9", "hitApi", "Schedule launch posts (Buffer)", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            {
              key: "text",
              value:
                "{launch_name} is here! {product_description}. Learn more: {utm_links}",
            },
            { key: "profile_ids[]", value: "{buffer_profile_ids}" },
            { key: "scheduled_at", value: "{launch_date}T09:00:00Z" },
          ],
          variables: [
            {
              name: "updates[0].id",
              customName: "buffer_scheduled_id",
              type: "string",
            },
            {
              name: "success",
              customName: "buffer_scheduled_success",
              type: "boolean",
            },
          ],
        },
      }),

      // 10. Master launch plan delivery (email)
      emailNode(
        "10",
        "Deliver master launch plan",
        2200,
        "{marketing_team_email}",
        "Launch Plan Ready: {launch_name} — {days_until_launch} days to launch",
        "<h2>Master Launch Plan: {launch_name}</h2><p><strong>Launch Date:</strong> {launch_date} ({days_until_launch} days away)</p><p><strong>Budget:</strong> ${budget_usd}</p><p><strong>Timeline:</strong> {timeline_summary}</p><hr><h3>Channel Strategy</h3><p><strong>Top Channels:</strong> {top_5_channels}</p><p><strong>Budget Allocation:</strong> {budget_allocation}</p><p>{launch_strategy_summary}</p><hr><h3>Content Plan</h3><p>{content_calendar_summary}</p><p><strong>Total Content Pieces:</strong> {total_content_pieces}</p><hr><h3>Creative Briefs</h3><p><strong>Visual Theme:</strong> {key_visual_theme}</p><p>{creative_briefs}</p><hr><h3>Tracking</h3><pre>{utm_links}</pre><hr><p><strong>Asana Project:</strong> <a href='{asana_project_url}'>View Launch Board</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary:
      "Comprehensive multi-platform launch planner: AI prioritizes channels and allocates budget, generates platform-specific content plans, builds timelines, creates Asana project, generates creative briefs, sets up UTM tracking, schedules posts via Buffer, and delivers a master launch plan.",
  },

  // ===================================================================
  // 864. A/B TEST RESULTS CALCULATOR
  // ===================================================================
  "A/B Test Results Calculator": {
    nodes: [
      trigger("Test Completion Trigger", "webhook_app", {
        source: "Experimentation Platform",
        event: "test.completed",
        howItStarts:
          "An A/B test reaches its target sample size or scheduled end date",
        setup:
          "In your experimentation platform (Optimizely, LaunchDarkly, or custom), configure a webhook for test.completed events. Include test_id, test_name, metric_name, and variant_count.",
        inputFields: [
          { name: "test_id", type: "string", example: "exp-2026-0042" },
          {
            name: "test_name",
            type: "string",
            example: "Checkout button color test",
          },
          { name: "metric_name", type: "string", example: "conversion_rate" },
          { name: "variant_count", type: "number", example: "2" },
        ],
      }),

      // 2. Fetch test config + raw data
      n("2", "hitApi", "Fetch test results data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.optimizely.com/v2/experiments/{test_id}/results",
          headers: [
            { key: "Authorization", value: "Bearer {optimizely_token}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "reach.variations[0].count",
              customName: "control_visitors",
              type: "number",
            },
            {
              name: "reach.variations[0].conversions",
              customName: "control_conversions",
              type: "number",
            },
            {
              name: "reach.variations[1].count",
              customName: "variant_visitors",
              type: "number",
            },
            {
              name: "reach.variations[1].conversions",
              customName: "variant_conversions",
              type: "number",
            },
            {
              name: "confidence_threshold",
              customName: "confidence_threshold",
              type: "number",
            },
            {
              name: "start_time",
              customName: "test_start_time",
              type: "string",
            },
            { name: "end_time", customName: "test_end_time", type: "string" },
          ],
        },
      }),

      // 3. Statistical significance calculation
      n("3", "runCode", "Calculate statistical significance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate Z-score and statistical significance for A/B test
const cV = parseInt(vars.control_visitors) || 0;
const cC = parseInt(vars.control_conversions) || 0;
const vV = parseInt(vars.variant_visitors) || 0;
const vC = parseInt(vars.variant_conversions) || 0;
const pC = cV > 0 ? cC / cV : 0;
const pV = vV > 0 ? vC / vV : 0;
const pPool = (cC + vC) / (cV + vV);
const se = Math.sqrt(pPool * (1 - pPool) * (1/cV + 1/vV));
const zScore = se > 0 ? (pV - pC) / se : 0;
// Approximate p-value from z-score using normal CDF approximation
const absZ = Math.abs(zScore);
const pValue = Math.exp(-0.5 * absZ * absZ) / (absZ * Math.sqrt(2 * Math.PI));
const confidenceLevel = (1 - pValue * 2) * 100;
const threshold = parseFloat(vars.confidence_threshold) || 95;
const isSignificant = confidenceLevel >= threshold;
const lift = pC > 0 ? ((pV - pC) / pC * 100).toFixed(2) : "0";
const result = {
  control_rate: (pC * 100).toFixed(2),
  variant_rate: (pV * 100).toFixed(2),
  absolute_lift: ((pV - pC) * 100).toFixed(2),
  relative_lift_pct: lift,
  z_score: zScore.toFixed(4),
  p_value: pValue.toFixed(6),
  confidence_level: confidenceLevel.toFixed(1),
  is_significant: isSignificant,
  winner: isSignificant ? (pV > pC ? "variant" : "control") : "inconclusive",
  total_sample_size: cV + vV
};
return result;`,
          outputVariables: [
            { name: "control_rate", type: "string" },
            { name: "variant_rate", type: "string" },
            { name: "absolute_lift", type: "string" },
            { name: "relative_lift_pct", type: "string" },
            { name: "z_score", type: "string" },
            { name: "p_value", type: "string" },
            { name: "confidence_level", type: "string" },
            { name: "is_significant", type: "boolean" },
            { name: "winner", type: "string" },
            { name: "total_sample_size", type: "number" },
          ],
        },
      }),

      // 4. Winner determination condition
      n("4", "condition", "Statistically significant winner?", 760, {
        conditionData: [
          {
            variable: "is_significant",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI results interpretation
      aiNode(
        "5",
        "AI interpret test results",
        1000,
        "You are a conversion rate optimization expert. Interpret these A/B test results for a non-technical audience. Explain what the numbers mean, whether the result is trustworthy, what the business impact is, and what should be done next. Respond JSON: {plain_english_summary: string, business_impact: string, recommendation: string, caveats: string, next_steps: string}",
        "Test: {test_name}. Metric: {metric_name}. Control rate: {control_rate}%. Variant rate: {variant_rate}%. Lift: {relative_lift_pct}%. Z-score: {z_score}. P-value: {p_value}. Confidence: {confidence_level}%. Winner: {winner}. Sample size: {total_sample_size}. Duration: {test_start_time} to {test_end_time}.",
        [
          {
            field: "plain_english_summary",
            as: "plain_english_summary",
            type: "string",
          },
          { field: "business_impact", as: "business_impact", type: "string" },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
          { field: "caveats", as: "result_caveats", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      // 6. Visual report generation
      n("6", "runCode", "Generate results report", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate formatted test results report
const result = {
  report_title: "A/B Test Results: " + vars.test_name,
  report_body: "Test: " + vars.test_name + " (" + vars.test_id + ")\\n" +
    "Metric: " + vars.metric_name + "\\n" +
    "Duration: " + vars.test_start_time + " to " + vars.test_end_time + "\\n" +
    "Sample Size: " + vars.total_sample_size + "\\n\\n" +
    "Control: " + vars.control_rate + "% (" + vars.control_conversions + "/" + vars.control_visitors + ")\\n" +
    "Variant: " + vars.variant_rate + "% (" + vars.variant_conversions + "/" + vars.variant_visitors + ")\\n\\n" +
    "Lift: " + vars.relative_lift_pct + "% | Confidence: " + vars.confidence_level + "%\\n" +
    "Winner: " + vars.winner + "\\n\\n" +
    vars.plain_english_summary + "\\n\\n" +
    "Recommendation: " + vars.ai_recommendation,
  report_generated: true
};
return result;`,
          outputVariables: [
            { name: "report_title", type: "string" },
            { name: "report_body", type: "string" },
            { name: "report_generated", type: "boolean" },
          ],
        },
      }),

      // 7. Results distribution (email + Slack)
      emailNode(
        "7",
        "Email test results report",
        1480,
        "{product_team_email}",
        "[{winner}] A/B Test Results: {test_name} — {relative_lift_pct}% lift",
        "<h2>{report_title}</h2><pre>{report_body}</pre><hr><h3>Business Impact</h3><p>{business_impact}</p><h3>Recommendation</h3><p>{ai_recommendation}</p><h3>Caveats</h3><p>{result_caveats}</p><h3>Next Steps</h3><p>{next_steps}</p>",
      ),

      // 8. Log to experimentation tracker (Sheets)
      sheetsNode(
        "8",
        "Log to experimentation tracker (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{test_id}","{test_name}","{metric_name}","{control_rate}","{variant_rate}","{relative_lift_pct}","{confidence_level}","{winner}","{ai_recommendation}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "tracker_rows",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "A/B test results calculator: fetches test data from Optimizely, performs Z-test for statistical significance, uses AI to interpret results in plain English, generates reports, distributes via email and Slack, and logs to an experimentation tracker.",
  },

  // ===================================================================
  // 865. ABM ACCOUNT-BASED AD CAMPAIGN
  // ===================================================================
  "ABM Account-Based Ad Campaign": {
    nodes: [
      trigger("Target Account List Trigger", "webhook_app", {
        source: "CRM / ABM Platform",
        event: "account_list.activated",
        howItStarts:
          "A target account list is activated for an ABM ad campaign with tier assignments",
        setup:
          "In your CRM or ABM platform, configure a webhook when a target account list is activated for ads. Include list_id, campaign_name, budget, and account data.",
        inputFields: [
          { name: "list_id", type: "string", example: "abm-list-2026-q1" },
          {
            name: "campaign_name",
            type: "string",
            example: "Enterprise AI Platform Q1",
          },
          { name: "total_budget_usd", type: "number", example: "25000" },
          {
            name: "account_ids",
            type: "string",
            example: "acc-001,acc-002,acc-003",
          },
        ],
      }),

      // 2. Clearbit account enrichment
      n("2", "hitApi", "Enrich accounts (Clearbit)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://company.clearbit.com/v2/companies/find?domain={account_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "geo.country", customName: "hq_country", type: "string" },
            { name: "tech", customName: "tech_stack_raw", type: "string" },
            {
              name: "linkedin.handle",
              customName: "linkedin_company_page",
              type: "string",
            },
          ],
        },
      }),

      // 3. Tier segmentation
      n("3", "runCode", "Segment accounts by tier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment accounts into tiers based on enrichment data
const employees = parseInt(vars.employee_count) || 0;
const revenue = parseInt(vars.annual_revenue) || 0;
let tier, budgetPct, adFormat;
if (employees >= 5000 || revenue >= 500000000) {
  tier = "tier_1"; budgetPct = 50; adFormat = "personalized";
} else if (employees >= 500 || revenue >= 50000000) {
  tier = "tier_2"; budgetPct = 35; adFormat = "industry_specific";
} else {
  tier = "tier_3"; budgetPct = 15; adFormat = "broad_abm";
}
const totalBudget = parseInt(vars.total_budget_usd) || 25000;
const tierBudget = Math.round(totalBudget * budgetPct / 100);
const result = {
  account_tier: tier,
  tier_budget_usd: tierBudget,
  budget_allocation_pct: budgetPct,
  ad_format_strategy: adFormat,
  tier_label: tier === "tier_1" ? "Strategic" : tier === "tier_2" ? "Scale" : "Programmatic",
  linkedin_budget: Math.round(tierBudget * 0.5),
  google_budget: Math.round(tierBudget * 0.3),
  meta_budget: Math.round(tierBudget * 0.2)
};
return result;`,
          outputVariables: [
            { name: "account_tier", type: "string" },
            { name: "tier_budget_usd", type: "number" },
            { name: "budget_allocation_pct", type: "number" },
            { name: "ad_format_strategy", type: "string" },
            { name: "tier_label", type: "string" },
            { name: "linkedin_budget", type: "number" },
            { name: "google_budget", type: "number" },
            { name: "meta_budget", type: "number" },
          ],
        },
      }),

      // 4. AI company-specific creative
      aiNode(
        "4",
        "AI generate company-specific ad creative",
        760,
        "You are an ABM creative strategist. Generate highly personalized ad creative for this specific account. Reference their industry, company size, tech stack, and pain points. Create variations for LinkedIn (professional), Google Display (concise), and Meta (engaging). Respond JSON: {linkedin_headline: string, linkedin_body: string, google_headline: string, google_description: string, meta_primary_text: string, meta_headline: string, cta_text: string, personalization_angle: string}",
        "Company: {company_name}. Industry: {industry}. Employees: {employee_count}. Revenue: {annual_revenue}. Tech stack: {tech_stack_raw}. Tier: {tier_label}. Ad format: {ad_format_strategy}. Campaign: {campaign_name}.",
        [
          {
            field: "linkedin_headline",
            as: "linkedin_headline",
            type: "string",
          },
          { field: "linkedin_body", as: "linkedin_body", type: "string" },
          { field: "google_headline", as: "google_headline", type: "string" },
          {
            field: "google_description",
            as: "google_description",
            type: "string",
          },
          {
            field: "meta_primary_text",
            as: "meta_primary_text",
            type: "string",
          },
          { field: "meta_headline", as: "meta_headline", type: "string" },
          { field: "cta_text", as: "ad_cta_text", type: "string" },
          {
            field: "personalization_angle",
            as: "personalization_angle",
            type: "string",
          },
        ],
      ),

      // 5. LinkedIn campaign creation
      n("5", "hitApi", "Create LinkedIn Ads campaign", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/adCampaignsV2",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "account",
              value: "urn:li:sponsoredAccount:{linkedin_ad_account_id}",
            },
            { key: "name", value: "ABM: {company_name} — {campaign_name}" },
            {
              key: "dailyBudget",
              value: '{"amount":"{linkedin_budget}","currencyCode":"USD"}',
            },
            {
              key: "targetingCriteria",
              value:
                '{"include":{"and":[{"or":{"urn:li:adTargetingFacet:employers":["urn:li:company:{linkedin_company_page}"]}}]}}',
            },
            { key: "type", value: "SPONSORED_UPDATES" },
            { key: "status", value: "PAUSED" },
          ],
          variables: [
            { name: "id", customName: "linkedin_campaign_id", type: "string" },
            {
              name: "status",
              customName: "linkedin_campaign_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Google Display campaign
      n("6", "hitApi", "Create Google Display campaign", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaigns:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "operations",
              value:
                '[{"create":{"name":"ABM: {company_name} — {campaign_name}","advertisingChannelType":"DISPLAY","campaignBudget":"customers/{google_ads_customer_id}/campaignBudgets/{budget_id}","status":"PAUSED"}}]',
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "google_campaign_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Meta audience sync
      n("7", "hitApi", "Create Meta custom audience", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_ad_account_id}/customaudiences",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "access_token", value: "{meta_ads_token}" },
            { key: "name", value: "ABM: {company_name} — {campaign_name}" },
            { key: "subtype", value: "CUSTOM" },
            {
              key: "description",
              value:
                "ABM target audience for {company_name} ({tier_label} tier)",
            },
            { key: "customer_file_source", value: "USER_PROVIDED_ONLY" },
          ],
          variables: [
            { name: "id", customName: "meta_audience_id", type: "string" },
          ],
        },
      }),

      // 8. Account engagement monitoring (Sheets)
      sheetsNode(
        "8",
        "Log campaign setup to tracker (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{list_id}","{company_name}","{account_tier}","{tier_label}","{tier_budget_usd}","{linkedin_campaign_id}","{google_campaign_id}","{meta_audience_id}","{personalization_angle}","{ad_cta_text}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "tracker_logged",
            type: "number",
          },
        ],
      ),

      // 9. Sales team Slack report
      slackNode(
        "9",
        "Report to sales team (Slack)",
        1960,
        ":dart: *ABM Campaign Created: {company_name}*\n\n*Campaign:* {campaign_name}\n*Tier:* {tier_label} ({account_tier})\n*Budget:* ${tier_budget_usd} (LinkedIn: ${linkedin_budget} / Google: ${google_budget} / Meta: ${meta_budget})\n*Personalization:* {personalization_angle}\n\n*LinkedIn Campaign:* {linkedin_campaign_id}\n*Google Campaign:* {google_campaign_id}\n*Meta Audience:* {meta_audience_id}\n\nCampaigns created in PAUSED state — review creative and activate.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "ABM ad campaign builder: enriches accounts via Clearbit, segments by tier, generates AI-personalized creative, creates campaigns on LinkedIn, Google Display, and Meta, logs to a tracker, and reports to sales via Slack.",
  },

  // ===================================================================
  // 866. ABM TARGET ACCOUNT LIST BUILDER
  // ===================================================================
  "ABM Target Account List Builder": {
    nodes: [
      trigger("Quarterly ABM Review Trigger", "schedule", {
        schedule: "Quarterly on first Monday at 09:00 UTC",
        howItStarts:
          "Quarterly review and refresh of the ABM target account list based on ICP and pipeline data",
        setup:
          "Schedule to run quarterly. Configure ICP criteria in environment variables: target_industries, min_employee_count, min_revenue, target_regions. Ensure CRM access is configured.",
        inputFields: [
          {
            name: "target_industries",
            type: "string",
            example: "SaaS,FinTech,HealthTech",
          },
          { name: "min_employee_count", type: "number", example: "200" },
          { name: "min_revenue_usd", type: "number", example: "10000000" },
          {
            name: "target_regions",
            type: "string",
            example: "North America,Europe",
          },
          { name: "max_list_size", type: "number", example: "500" },
        ],
      }),

      // 2. Customer profile analysis
      n("2", "hitApi", "Analyze existing customer profiles (HubSpot)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/companies/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
            },
            {
              key: "properties",
              value:
                '["name","industry","numberofemployees","annualrevenue","country","domain","hs_lead_status"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            {
              name: "results",
              customName: "existing_customers_raw",
              type: "string",
            },
            {
              name: "total",
              customName: "total_existing_customers",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI account scoring
      aiNode(
        "3",
        "AI score and identify lookalike accounts",
        520,
        "You are an ABM strategist. Analyze the existing customer base to identify the ideal customer profile (ICP). Then score potential target accounts based on fit with ICP, considering: industry match, company size, revenue, geographic alignment, and technology fit. Respond JSON: {icp_summary: string, scoring_criteria: string, ideal_company_size: string, ideal_industries: string, ideal_revenue_range: string, confidence_score: number}",
        "Existing customers ({total_existing_customers}): {existing_customers_raw}. Target industries: {target_industries}. Min employees: {min_employee_count}. Min revenue: {min_revenue_usd}. Target regions: {target_regions}.",
        [
          { field: "icp_summary", as: "icp_summary", type: "string" },
          { field: "scoring_criteria", as: "scoring_criteria", type: "string" },
          {
            field: "ideal_company_size",
            as: "ideal_company_size",
            type: "string",
          },
          { field: "ideal_industries", as: "ideal_industries", type: "string" },
          {
            field: "ideal_revenue_range",
            as: "ideal_revenue_range",
            type: "string",
          },
          { field: "confidence_score", as: "icp_confidence", type: "number" },
        ],
      ),

      // 4. Account ranking
      n("4", "hitApi", "Search & rank accounts (Apollo)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_companies/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_key}" },
            {
              key: "organization_num_employees_ranges",
              value: '["200,1000","1001,5000","5001,"]',
            },
            {
              key: "organization_industry_tag_ids",
              value: "{target_industries}",
            },
            { key: "per_page", value: "100" },
          ],
          variables: [
            {
              name: "organizations",
              customName: "candidate_accounts_raw",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_candidates",
              type: "number",
            },
          ],
        },
      }),

      // 5. Pipeline dedupe
      n("5", "runCode", "Dedupe against existing pipeline", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplicate candidate accounts against existing customers and pipeline
const candidates = JSON.parse(vars.candidate_accounts_raw || "[]");
const existingCustomers = JSON.parse(vars.existing_customers_raw || "[]");
const existingDomains = new Set(existingCustomers.map(c => (c.properties?.domain || "").toLowerCase()));
const deduped = candidates.filter(c => {
  const domain = (c.primary_domain || c.domain || "").toLowerCase();
  return domain && !existingDomains.has(domain);
});
const maxSize = parseInt(vars.max_list_size) || 500;
const finalList = deduped.slice(0, maxSize);
const result = {
  deduped_count: finalList.length,
  removed_duplicates: candidates.length - deduped.length,
  final_list_json: JSON.stringify(finalList.map(c => ({
    name: c.name, domain: c.primary_domain || c.domain,
    employees: c.estimated_num_employees, industry: c.industry
  }))),
  meets_minimum: finalList.length >= 50,
  list_quality_score: Math.min(100, Math.round((finalList.length / maxSize) * 100))
};
return result;`,
          outputVariables: [
            { name: "deduped_count", type: "number" },
            { name: "removed_duplicates", type: "number" },
            { name: "final_list_json", type: "string" },
            { name: "meets_minimum", type: "boolean" },
            { name: "list_quality_score", type: "number" },
          ],
        },
      }),

      // 6. Minimum list size condition
      n("6", "condition", "Minimum list size met?", 1240, {
        conditionData: [
          {
            variable: "meets_minimum",
            value: "true",
            comparison_operator: "EQUALS",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Account dossier generation
      aiNode(
        "7",
        "AI generate account dossiers",
        1480,
        "You are an ABM research analyst. For the target account list, generate a brief dossier summary highlighting why each top account is a good fit, their likely pain points, and a suggested engagement approach. Focus on the top 10 accounts. Respond JSON: {dossier_summary: string, top_10_accounts: string, engagement_playbook: string, list_readiness_score: number}",
        "ICP: {icp_summary}. Target list ({deduped_count} accounts): {final_list_json}. Scoring criteria: {scoring_criteria}. Quality score: {list_quality_score}.",
        [
          { field: "dossier_summary", as: "dossier_summary", type: "string" },
          { field: "top_10_accounts", as: "top_10_accounts", type: "string" },
          {
            field: "engagement_playbook",
            as: "engagement_playbook",
            type: "string",
          },
          {
            field: "list_readiness_score",
            as: "list_readiness_score",
            type: "number",
          },
        ],
      ),

      // 8. Final list distribution (Sheets + Slack)
      sheetsNode(
        "8",
        "Publish final list (Sheets + Slack)",
        1720,
        [{ key: "values", value: "{final_list_json}" }],
        [
          {
            name: "updates.updatedRows",
            customName: "list_rows_published",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "ABM target account list builder: analyzes existing customers in HubSpot, uses AI to define ICP and scoring criteria, searches Apollo for lookalike accounts, dedupes against pipeline, generates account dossiers, and publishes the final list.",
  },

  // ===================================================================
  // 867. ABANDONED CART RECOVERY
  // ===================================================================
  "Abandoned Cart Recovery": {
    nodes: [
      trigger("Cart Abandonment Trigger", "webhook_app", {
        source: "Shopify",
        event: "checkouts/create",
        howItStarts:
          "A customer adds items to cart and begins checkout but does not complete purchase within the configured window",
        setup:
          "In Shopify -> Settings -> Notifications -> Webhooks, add a webhook for checkouts/create. The workflow uses a delay to wait before sending recovery messages. Include checkout_id, customer_email, and total_price.",
        inputFields: [
          { name: "checkout_id", type: "string", example: "chk_abc123def456" },
          {
            name: "customer_email",
            type: "string",
            example: "customer@example.com",
          },
          { name: "customer_first_name", type: "string", example: "Sarah" },
          { name: "total_price", type: "string", example: "149.99" },
          {
            name: "line_items_json",
            type: "string",
            example: '[{"title":"Widget Pro","quantity":1,"price":"149.99"}]',
          },
          { name: "customer_phone", type: "string", example: "+15559876543" },
        ],
      }),

      // 2. Wait period (delay)
      n("2", "delayAction", "Wait 1 hour before recovery", 280, {
        delayAction: { time: 1, timeFormat: "hours" },
      }),

      // 3. Cart + customer profile fetch
      n("3", "hitApi", "Fetch cart & customer profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/checkouts/{checkout_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            {
              name: "checkout.completed_at",
              customName: "checkout_completed_at",
              type: "string",
            },
            {
              name: "checkout.abandoned_checkout_url",
              customName: "recovery_url",
              type: "string",
            },
            {
              name: "checkout.total_price",
              customName: "cart_total",
              type: "string",
            },
            {
              name: "checkout.line_items",
              customName: "cart_items_raw",
              type: "string",
            },
            {
              name: "checkout.currency",
              customName: "cart_currency",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI incentive decision
      aiNode(
        "4",
        "AI decide recovery incentive",
        760,
        "You are an e-commerce recovery specialist. Decide the optimal recovery strategy for this abandoned cart. Consider the cart value, items, and customer history. Decide whether to offer a discount (and how much), free shipping, urgency messaging, or social proof. Write a personalized recovery email subject and body. Respond JSON: {incentive_type: string, discount_pct: number, email_subject: string, email_body_html: string, urgency_message: string, recovery_strategy: string}",
        "Customer: {customer_first_name}. Cart total: {cart_total} {cart_currency}. Items: {cart_items_raw}. Recovery URL: {recovery_url}.",
        [
          { field: "incentive_type", as: "incentive_type", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "number" },
          {
            field: "email_subject",
            as: "recovery_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "recovery_email_body",
            type: "string",
          },
          { field: "urgency_message", as: "urgency_message", type: "string" },
          {
            field: "recovery_strategy",
            as: "recovery_strategy",
            type: "string",
          },
        ],
      ),

      // 5. Personalized recovery email
      emailNode(
        "5",
        "Send recovery email",
        1000,
        "{customer_email}",
        "{recovery_email_subject}",
        "{recovery_email_body}",
      ),

      // 6. 24hr recovery check condition
      n("6", "delayAction", "Wait 24 hours for recovery", 1240, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 7. SMS follow-up (Twilio)
      n("7", "hitApi", "SMS follow-up (Twilio)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{customer_phone}" },
            { key: "From", value: "{twilio_phone}" },
            {
              key: "Body",
              value:
                "Hi {customer_first_name}! Your cart ({cart_total} {cart_currency}) is still waiting. {urgency_message} Complete your order: {recovery_url}",
            },
          ],
          variables: [
            { name: "sid", customName: "sms_sid", type: "string" },
            {
              name: "status",
              customName: "sms_delivery_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Attribution logging (Sheets)
      sheetsNode(
        "8",
        "Log recovery attribution (Sheets)",
        1720,
        [
          {
            key: "values",
            value:
              '[["{checkout_id}","{customer_email}","{cart_total}","{incentive_type}","{discount_pct}","{recovery_strategy}","{sms_delivery_status}"]]',
          },
        ],
        [
          {
            name: "updates.updatedRows",
            customName: "attribution_logged",
            type: "number",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Abandoned cart recovery: waits 1 hour after cart creation, fetches cart details from Shopify, uses AI to decide optimal incentive and craft personalized email, sends recovery email, follows up with SMS via Twilio after 24 hours, and logs recovery attribution.",
  },
};
